// src/components/button.tsx
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";

// src/lib/cn.ts
import { clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";
import { SEMANTIC_TOKEN_NAMES, typography } from "@profylr/design-tokens";
var FONT_SIZES = Object.keys(typography).filter(
  (name) => name !== "families" && name !== "weights"
);
var TEXT_COLOURS = [
  ...SEMANTIC_TOKEN_NAMES,
  "accent",
  "accent-bright",
  "accent-hover",
  "accent-subtle",
  "accent-foreground",
  "focus",
  "focus-outer"
];
var twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [{ text: FONT_SIZES }],
      "text-color": [{ text: TEXT_COLOURS }]
    }
  }
});
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
var FOCUS_RING = "focus-ring outline-none";
var FOCUS_RING_WITHIN = "focus-ring-within outline-none";
var OVERLAY_SURFACE = "z-50 rounded-lg border border-divider bg-surface-raised shadow-overlay";

// src/components/button.tsx
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md",
    "font-medium transition-colors duration-fast ease-move",
    "disabled:pointer-events-none disabled:opacity-50",
    // Icons inside a button should never be focusable or announced separately.
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:size-4",
    FOCUS_RING
  ),
  {
    variants: {
      variant: {
        /** The primary action. Purple fill, white label, in every app. */
        primary: "bg-action text-action-foreground hover:bg-action-hover",
        /**
         * Secondary: neutral outline. Raised, not canvas: with the tinted
         * light canvas, a canvas-filled button sitting on a white card
         * renders darker than its container and reads disabled. Raised is
         * white in light -- white means actionable -- and the lifted slate
         * in dark, and the hover drop to sunken is visible in both.
         */
        secondary: "border border-control-border bg-surface-raised text-text hover:bg-surface-sunken",
        /** Tertiary: ghost, unchanged across apps. */
        ghost: "text-text hover:bg-surface-sunken",
        /**
         * Destructive actions borrow the breach role because that is what they
         * are -- this is colour carrying meaning, per section 3.9, not
         * decoration.
         */
        /*
         * text-status-breach-foreground, not text-inverse: white in light,
         * deep purple in dark, because white on the dark breach red fails at
         * 2.79:1. The token pair is gated in @profylr/design-tokens.
         */
        destructive: "bg-status-breach text-status-breach-foreground hover:opacity-90",
        /** A link-styled action, for inline "view detail" affordances. */
        link: "text-action underline underline-offset-4 hover:text-action-hover"
      },
      size: {
        /**
         * Default height tracks the density switch, so a button inside a
         * compact regulatory table shrinks with the rows around it.
         */
        default: "h-control px-4 text-body",
        sm: "h-8 px-3 text-body-sm",
        lg: "h-10 px-6 text-body",
        /** Square, for a lone icon. Still a 32px minimum target. */
        icon: "size-8",
        /**
         * Square at control height, for an icon button beside an input --
         * the "+" that adds a row. Tracks the density switch with the
         * control it sits next to, so the two never drift apart.
         */
        "icon-control": "h-control aspect-square px-0"
      }
    },
    defaultVariants: {
      variant: "primary",
      size: "default"
    }
  }
);
var Button = React.forwardRef(
  ({ className, variant, size, asChild = false, type, pending = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(
      Comp,
      {
        ref,
        className: cn(buttonVariants({ variant, size }), className),
        type: asChild ? type : type ?? "button",
        disabled: disabled || pending,
        "aria-busy": pending || void 0,
        ...props,
        children: asChild ? children : /* @__PURE__ */ jsxs(Fragment, { children: [
          pending ? /* @__PURE__ */ jsx(Loader2, { className: "animate-spin", "aria-hidden": "true" }) : null,
          children
        ] })
      }
    );
  }
);
Button.displayName = "Button";

// src/components/input.tsx
import * as React5 from "react";

// src/components/field.tsx
import { CircleAlert, Info } from "lucide-react";
import * as React4 from "react";

// src/components/label.tsx
import * as LabelPrimitive from "@radix-ui/react-label";
import { cva as cva2 } from "class-variance-authority";
import * as React2 from "react";
import { Fragment as Fragment2, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var labelVariants = cva2("", {
  variants: {
    variant: {
      /** A form field label. */
      default: "text-body-small font-medium text-text",
      /**
       * Section 3.5's uppercase letterspaced micro-label -- "it belongs on
       * table headers, KPI tile labels and section eyebrows". Section 3.6 leans
       * on it too: an eyebrow plus whitespace replaces a container.
       */
      micro: "micro-label"
    }
  },
  defaultVariants: { variant: "default" }
});
var Label = React2.forwardRef(({ className, variant, required, children, ...props }, ref) => /* @__PURE__ */ jsxs2(
  LabelPrimitive.Root,
  {
    ref,
    className: cn(
      labelVariants({ variant }),
      "peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
      className
    ),
    ...props,
    children: [
      children,
      required ? /* @__PURE__ */ jsxs2(Fragment2, { children: [
        /* @__PURE__ */ jsx2("span", { "aria-hidden": "true", className: "ml-0.5 text-status-breach", children: "*" }),
        /* @__PURE__ */ jsx2("span", { className: "sr-only", children: " (required)" })
      ] }) : null
    ]
  }
));
Label.displayName = LabelPrimitive.Root.displayName;

// src/components/popover.tsx
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as React3 from "react";
import { jsx as jsx3 } from "react/jsx-runtime";
var Popover = PopoverPrimitive.Root;
var PopoverTrigger = PopoverPrimitive.Trigger;
var PopoverAnchor = PopoverPrimitive.Anchor;
var PopoverClose = PopoverPrimitive.Close;
var PopoverPortal = PopoverPrimitive.Portal;
var PopoverContent = React3.forwardRef(({ className, align = "start", sideOffset = 6, children, ...props }, ref) => /* @__PURE__ */ jsx3(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx3(
  PopoverPrimitive.Content,
  {
    ref,
    align,
    sideOffset,
    className: cn(
      OVERLAY_SURFACE,
      "w-72 p-3 text-body-sm text-text outline-none",
      "transition-[opacity,translate] duration-fast ease-move",
      "data-[state=closed]:opacity-0 data-[state=open]:opacity-100",
      "data-[state=closed]:-translate-y-1 data-[state=open]:translate-y-0",
      className
    ),
    ...props,
    children
  }
) }));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

// src/components/field.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var FieldContext = React4.createContext(null);
function useFieldContext() {
  return React4.useContext(FieldContext);
}
function useFieldControl(props) {
  const field = React4.useContext(FieldContext);
  if (!field) return props;
  return {
    ...props,
    id: props.id ?? field.id,
    "aria-describedby": props["aria-describedby"] ?? field.describedBy,
    "aria-invalid": props["aria-invalid"] ?? (field.invalid || void 0),
    "aria-required": props["aria-required"] ?? (field.required || void 0),
    disabled: props.disabled ?? (field.disabled || void 0)
  };
}
function Field({
  label,
  id: idProp,
  required = false,
  disabled = false,
  error,
  hint,
  help,
  helpLabel,
  readOnly = false,
  value,
  counter = true,
  className,
  children
}) {
  const generated = React4.useId();
  const id = idProp ?? `field-${generated}`;
  const labelId = `${id}-label`;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const helpId = `${id}-help`;
  const [helpOpen, setHelpOpen] = React4.useState(false);
  const [length, setLength] = React4.useState(null);
  const reportLength = React4.useCallback((next, max) => {
    setLength(
      (prev) => prev && prev.length === next && prev.max === max ? prev : { length: next, max }
    );
  }, []);
  const describedBy = [
    hint ? hintId : null,
    error ? errorId : null,
    // The help content only exists in the document while it is open.
    help && helpOpen ? helpId : null
  ].filter(Boolean).join(" ") || void 0;
  const context = React4.useMemo(
    () => ({
      id,
      labelId,
      describedBy,
      invalid: Boolean(error),
      required,
      disabled,
      reportLength
    }),
    [id, labelId, describedBy, error, required, disabled, reportLength]
  );
  const helpName = helpLabel ?? (typeof label === "string" ? `About ${label}` : "About this field");
  if (readOnly) {
    return /* @__PURE__ */ jsxs3("div", { className: cn("flex flex-col gap-2", className), children: [
      /* @__PURE__ */ jsx4("span", { id: labelId, className: "text-body-sm font-medium text-text", children: label }),
      /* @__PURE__ */ jsx4("p", { "aria-labelledby": labelId, className: "text-body text-text", children: value === void 0 || value === null || value === "" ? /* @__PURE__ */ jsx4("span", { className: "text-text-muted", "aria-label": "No value", children: "\u2014" }) : value })
    ] });
  }
  return /* @__PURE__ */ jsx4(FieldContext.Provider, { value: context, children: /* @__PURE__ */ jsxs3("div", { className: cn("flex flex-col gap-2", className), "data-invalid": error ? "" : void 0, children: [
    /* @__PURE__ */ jsxs3("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx4(Label, { id: labelId, htmlFor: id, required, children: label }),
      help ? /* @__PURE__ */ jsxs3(Popover, { open: helpOpen, onOpenChange: setHelpOpen, children: [
        /* @__PURE__ */ jsx4(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx4(
          "button",
          {
            type: "button",
            "aria-label": helpName,
            className: cn(
              "inline-flex size-6 items-center justify-center rounded-sm text-text-muted",
              "hover:text-text transition-colors duration-fast ease-move",
              FOCUS_RING
            ),
            children: /* @__PURE__ */ jsx4(Info, { className: "size-4", "aria-hidden": "true" })
          }
        ) }),
        /* @__PURE__ */ jsx4(PopoverContent, { "aria-label": helpName, children: /* @__PURE__ */ jsx4("div", { id: helpId, children: help }) })
      ] }) : null
    ] }),
    children,
    hint || error || counter && length ? /* @__PURE__ */ jsxs3("div", { className: "flex items-start justify-between gap-3", children: [
      /* @__PURE__ */ jsxs3("div", { className: "flex min-w-0 flex-col gap-1", children: [
        hint ? /* @__PURE__ */ jsx4("p", { id: hintId, className: "text-caption text-text-muted", children: hint }) : null,
        /* @__PURE__ */ jsx4("div", { "aria-live": "polite", children: error ? /* @__PURE__ */ jsxs3(
          "p",
          {
            id: errorId,
            className: "flex items-start gap-1 text-caption text-status-breach",
            children: [
              /* @__PURE__ */ jsx4(CircleAlert, { className: "mt-px size-3.5 shrink-0", "aria-hidden": "true" }),
              /* @__PURE__ */ jsx4("span", { children: error })
            ]
          }
        ) : null })
      ] }),
      counter && length ? /* @__PURE__ */ jsxs3(
        "p",
        {
          className: "shrink-0 font-mono text-caption text-text-muted",
          "data-numeric": true,
          "aria-live": "polite",
          "aria-atomic": "true",
          children: [
            /* @__PURE__ */ jsx4("span", { className: "sr-only", children: "Characters used: " }),
            length.length,
            " / ",
            length.max
          ]
        }
      ) : null
    ] }) : null
  ] }) });
}
Field.displayName = "Field";
var FieldGroup = React4.forwardRef(
  ({ label, description, className, children, ...props }, ref) => /* @__PURE__ */ jsxs3("fieldset", { ref, className: cn("flex min-w-0 flex-col gap-3", className), ...props, children: [
    /* @__PURE__ */ jsx4("legend", { className: "eyebrow mb-2", children: label }),
    description ? /* @__PURE__ */ jsx4("p", { className: "-mt-1 text-body-sm text-text-muted", children: description }) : null,
    children
  ] })
);
FieldGroup.displayName = "FieldGroup";

// src/components/input.tsx
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var Input = React5.forwardRef(
  ({
    className,
    type,
    invalid,
    describedBy,
    numeric,
    prefix,
    suffix,
    maxLength,
    onChange,
    ...props
  }, ref) => {
    const field = useFieldContext();
    const wired = useFieldControl({
      ...props,
      "aria-describedby": props["aria-describedby"] ?? describedBy
    });
    const isInvalid = invalid ?? Boolean(wired["aria-invalid"]);
    const report = (el) => {
      if (field && maxLength && el) field.reportLength(el.value.length, maxLength);
    };
    const control = /* @__PURE__ */ jsx5(
      "input",
      {
        ref: (el) => {
          report(el);
          if (typeof ref === "function") ref(el);
          else if (ref) ref.current = el;
        },
        type,
        maxLength,
        onChange: (e) => {
          report(e.currentTarget);
          onChange?.(e);
        },
        "data-numeric": numeric ? "" : void 0,
        className: cn(
          // control-surface, not surface: in light a control is white at rest --
          // white means editable -- and in dark it rests in the sunken well and
          // lifts to the raised surface on focus. One pair, no theme branch.
          "flex h-control w-full rounded-sm border bg-control-surface px-6 py-1",
          "focus:bg-control-surface-focus",
          "text-body text-text transition-colors duration-fast ease-move",
          "placeholder:text-text-muted",
          "file:border-0 file:bg-transparent file:text-body-sm file:font-medium file:text-text",
          "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-70",
          isInvalid ? "border-status-breach" : "border-control-border",
          numeric && "text-right font-mono",
          prefix && "pl-8",
          suffix && "pr-8",
          FOCUS_RING,
          className
        ),
        ...wired,
        "aria-invalid": isInvalid || void 0
      }
    );
    if (!prefix && !suffix) return control;
    return /* @__PURE__ */ jsxs4("div", { className: "relative flex w-full items-center", children: [
      prefix ? /* @__PURE__ */ jsx5(
        "span",
        {
          "aria-hidden": "true",
          className: "pointer-events-none absolute left-3 text-body-sm text-text-muted",
          children: prefix
        }
      ) : null,
      control,
      suffix ? /* @__PURE__ */ jsx5(
        "span",
        {
          "aria-hidden": "true",
          className: "pointer-events-none absolute right-3 font-mono text-body-sm text-text-muted",
          children: suffix
        }
      ) : null
    ] });
  }
);
Input.displayName = "Input";

// src/components/textarea.tsx
import * as React6 from "react";
import { jsx as jsx6 } from "react/jsx-runtime";
var Textarea = React6.forwardRef(
  ({ className, invalid, rows = 3, maxLength, onChange, ...props }, ref) => {
    const field = useFieldContext();
    const wired = useFieldControl(props);
    const isInvalid = invalid ?? Boolean(wired["aria-invalid"]);
    const report = (el) => {
      if (field && maxLength && el) field.reportLength(el.value.length, maxLength);
    };
    return /* @__PURE__ */ jsx6(
      "textarea",
      {
        ref: (el) => {
          report(el);
          if (typeof ref === "function") ref(el);
          else if (ref) ref.current = el;
        },
        rows,
        maxLength,
        onChange: (e) => {
          report(e.currentTarget);
          onChange?.(e);
        },
        className: cn(
          "flex w-full rounded-sm border bg-control-surface px-6 py-3",
          "focus:bg-control-surface-focus",
          "text-body text-text transition-colors duration-fast ease-move",
          "placeholder:text-text-muted resize-y",
          "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-70",
          isInvalid ? "border-status-breach" : "border-control-border",
          FOCUS_RING,
          className
        ),
        ...wired,
        "aria-invalid": isInvalid || void 0
      }
    );
  }
);
Textarea.displayName = "Textarea";

// src/components/checkbox.tsx
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check, Minus } from "lucide-react";
import * as React7 from "react";
import { jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
var Checkbox = React7.forwardRef(
  ({
    className,
    label,
    description,
    invalid,
    checked: checkedProp,
    defaultChecked,
    onCheckedChange,
    ...props
  }, ref) => {
    const field = useFieldContext();
    const generated = React7.useId();
    const wired = useFieldControl(props);
    const id = wired.id ?? `checkbox-${generated}`;
    const labelId = `${id}-option`;
    const descriptionId = `${id}-option-description`;
    const isInvalid = invalid ?? Boolean(wired["aria-invalid"]);
    const [uncontrolled, setUncontrolled] = React7.useState(
      defaultChecked ?? false
    );
    const checked = checkedProp ?? uncontrolled;
    const labelledBy = props["aria-labelledby"] ?? [field?.labelId, labelId].filter(Boolean).join(" ");
    const describedBy = [description ? descriptionId : null, wired["aria-describedby"]].filter(Boolean).join(" ") || void 0;
    return /* @__PURE__ */ jsxs5(
      "div",
      {
        className: cn(
          // 1.5rem, so the row is never shorter than the SC 2.5.8 target.
          "flex min-h-6 items-start gap-2",
          "has-disabled:cursor-not-allowed has-disabled:opacity-70",
          className
        ),
        children: [
          /* @__PURE__ */ jsx7("span", { className: "flex h-5 shrink-0 items-center", children: /* @__PURE__ */ jsx7(
            CheckboxPrimitive.Root,
            {
              ref,
              className: cn(
                "relative flex size-4 shrink-0 items-center justify-center rounded-sm border",
                // control-surface, not surface -- see Input for the two themes' logics.
                "bg-control-surface text-action-foreground",
                "transition-colors duration-fast ease-move",
                // Checked runs on the action purple in every app (section 3.7),
                // so a ticked box never looks like a breach badge next to OR's
                // crimson accent.
                "data-[state=checked]:border-action data-[state=checked]:bg-action",
                "data-[state=indeterminate]:border-action data-[state=indeterminate]:bg-action",
                "disabled:cursor-not-allowed",
                // SC 2.5.8: the box draws at 1rem, the target is 1.5rem. The
                // pseudo-element widens the hit area without touching the drawing.
                "before:absolute before:-inset-1",
                isInvalid ? "border-status-breach" : "border-control-border",
                FOCUS_RING
              ),
              checked: checkedProp,
              defaultChecked,
              onCheckedChange: (next) => {
                setUncontrolled(next);
                onCheckedChange?.(next);
              },
              ...wired,
              id,
              "aria-labelledby": labelledBy,
              "aria-describedby": describedBy,
              "aria-invalid": isInvalid || void 0,
              children: /* @__PURE__ */ jsx7(CheckboxPrimitive.Indicator, { className: "flex items-center justify-center text-current", children: checked === "indeterminate" ? /* @__PURE__ */ jsx7(Minus, { className: "size-3", strokeWidth: 3, "aria-hidden": "true" }) : /* @__PURE__ */ jsx7(Check, { className: "size-3", strokeWidth: 3, "aria-hidden": "true" }) })
            }
          ) }),
          /* @__PURE__ */ jsxs5("span", { className: "flex min-w-0 flex-col", children: [
            /* @__PURE__ */ jsx7("label", { id: labelId, htmlFor: id, className: "text-body text-text", children: label }),
            description ? /* @__PURE__ */ jsx7("span", { id: descriptionId, className: "text-caption text-text-muted", children: description }) : null
          ] })
        ]
      }
    );
  }
);
Checkbox.displayName = "Checkbox";

// src/components/radio-group.tsx
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Check as Check2 } from "lucide-react";
import * as React8 from "react";
import { jsx as jsx8, jsxs as jsxs6 } from "react/jsx-runtime";
var RadioGroupContext = React8.createContext({
  variant: "default",
  invalid: false
});
var RadioGroup = React8.forwardRef(({ className, variant = "default", invalid, orientation, ...props }, ref) => {
  const field = useFieldContext();
  const wired = useFieldControl(props);
  const labelledBy = props["aria-labelledby"] ?? field?.labelId;
  const isInvalid = invalid ?? Boolean(wired["aria-invalid"]);
  if (process.env.NODE_ENV !== "production") {
    if (!props["aria-label"] && !labelledBy) {
      console.warn(
        "[@profylr/ui] RadioGroup has no accessible name. A radiogroup is never named by <label for>. Wrap it in a Field, or pass aria-label / aria-labelledby."
      );
    }
  }
  const resolvedOrientation = orientation ?? (variant === "tiles" ? void 0 : "vertical");
  const context = React8.useMemo(
    () => ({ variant, invalid: isInvalid }),
    [variant, isInvalid]
  );
  return /* @__PURE__ */ jsx8(RadioGroupContext.Provider, { value: context, children: /* @__PURE__ */ jsx8(
    RadioGroupPrimitive.Root,
    {
      ref,
      orientation: resolvedOrientation,
      "data-variant": variant,
      className: cn(
        variant === "tiles" ? "grid gap-3" : resolvedOrientation === "horizontal" ? "flex flex-wrap gap-x-6 gap-y-2" : "flex flex-col gap-2",
        className
      ),
      ...wired,
      "aria-labelledby": labelledBy,
      "aria-invalid": isInvalid || void 0
    }
  ) });
});
RadioGroup.displayName = "RadioGroup";
var RadioGroupItem = React8.forwardRef(({ className, label, description, id: idProp, ...props }, ref) => {
  const { variant, invalid } = React8.useContext(RadioGroupContext);
  const generated = React8.useId();
  const id = idProp ?? `radio-${generated}`;
  const labelId = `${id}-label`;
  const descriptionId = `${id}-description`;
  const labelledBy = props["aria-labelledby"] ?? labelId;
  const describedBy = [description ? descriptionId : null, props["aria-describedby"]].filter(Boolean).join(" ") || void 0;
  if (variant === "tiles") {
    return /* @__PURE__ */ jsxs6(
      RadioGroupPrimitive.Item,
      {
        ref,
        id,
        className: cn(
          "relative flex min-h-control w-full items-center justify-between gap-3 rounded-md border px-4 py-2 text-left",
          "bg-control-surface text-text transition-colors duration-fast ease-move",
          "hover:bg-control-surface-focus",
          // Selected lifts to the raised surface behind an action-coloured
          // edge. The edge is 2px, not 1: the second pixel is a pseudo-element
          // drawn just inside the border, so layout does not shift and the
          // focus ring's box-shadow is left alone.
          "after:pointer-events-none after:absolute after:inset-0 after:rounded-md after:border after:border-action after:opacity-0",
          "after:transition-opacity after:duration-fast after:ease-move",
          "data-[state=checked]:border-action data-[state=checked]:bg-surface-raised",
          "data-[state=checked]:after:opacity-100",
          "disabled:cursor-not-allowed disabled:opacity-70",
          invalid ? "border-status-breach" : "border-control-border",
          FOCUS_RING,
          className
        ),
        ...props,
        "aria-labelledby": labelledBy,
        "aria-describedby": describedBy,
        children: [
          /* @__PURE__ */ jsxs6("span", { className: "flex min-w-0 flex-col", children: [
            /* @__PURE__ */ jsx8("span", { id: labelId, className: "text-body font-medium text-text", children: label }),
            description ? /* @__PURE__ */ jsx8("span", { id: descriptionId, className: "text-caption text-text-muted", children: description }) : null
          ] }),
          /* @__PURE__ */ jsxs6(RadioGroupPrimitive.Indicator, { className: "flex shrink-0 items-center gap-1 text-action", children: [
            /* @__PURE__ */ jsx8(Check2, { className: "size-4", "aria-hidden": "true" }),
            /* @__PURE__ */ jsx8("span", { className: "text-caption font-medium", children: "Selected" })
          ] })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs6(
    "div",
    {
      className: cn(
        // 1.5rem, so the row is never shorter than the SC 2.5.8 target.
        "flex min-h-6 items-start gap-2",
        "has-disabled:cursor-not-allowed has-disabled:opacity-70",
        className
      ),
      children: [
        /* @__PURE__ */ jsx8("span", { className: "flex h-5 shrink-0 items-center", children: /* @__PURE__ */ jsx8(
          RadioGroupPrimitive.Item,
          {
            ref,
            id,
            className: cn(
              "relative flex size-4 shrink-0 items-center justify-center rounded-pill border",
              "bg-control-surface transition-colors duration-fast ease-move",
              "data-[state=checked]:border-action",
              "disabled:cursor-not-allowed",
              // SC 2.5.8: the circle draws at 1rem, the target is 1.5rem.
              "before:absolute before:-inset-1",
              invalid ? "border-status-breach" : "border-control-border",
              FOCUS_RING
            ),
            ...props,
            "aria-labelledby": labelledBy,
            "aria-describedby": describedBy,
            children: /* @__PURE__ */ jsx8(RadioGroupPrimitive.Indicator, { className: "flex items-center justify-center", children: /* @__PURE__ */ jsx8("span", { className: "size-2 rounded-pill bg-action" }) })
          }
        ) }),
        /* @__PURE__ */ jsxs6("span", { className: "flex min-w-0 flex-col", children: [
          /* @__PURE__ */ jsx8("label", { id: labelId, htmlFor: id, className: "text-body text-text", children: label }),
          description ? /* @__PURE__ */ jsx8("span", { id: descriptionId, className: "text-caption text-text-muted", children: description }) : null
        ] })
      ]
    }
  );
});
RadioGroupItem.displayName = "RadioGroupItem";

// src/components/toast.tsx
import * as ToastPrimitive from "@radix-ui/react-toast";
import { cva as cva3 } from "class-variance-authority";
import { CircleAlert as CircleAlert2, CircleCheck, TriangleAlert, X } from "lucide-react";
import * as React9 from "react";
import { Fragment as Fragment3, jsx as jsx9, jsxs as jsxs7 } from "react/jsx-runtime";
var TOAST_LIMIT = 3;
var TOAST_REMOVE_DELAY = 1e3;
var count = 0;
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}
var toastTimeouts = /* @__PURE__ */ new Map();
function addToRemoveQueue(toastId) {
  if (toastTimeouts.has(toastId)) return;
  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({ type: "REMOVE_TOAST", toastId });
  }, TOAST_REMOVE_DELAY);
  toastTimeouts.set(toastId, timeout);
}
function reducer(state, action) {
  switch (action.type) {
    case "ADD_TOAST":
      return { ...state, toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT) };
    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) => t.id === action.toast.id ? { ...t, ...action.toast } : t)
      };
    case "DISMISS_TOAST": {
      const { toastId } = action;
      if (toastId) addToRemoveQueue(toastId);
      else state.toasts.forEach((t) => addToRemoveQueue(t.id));
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === toastId || toastId === void 0 ? { ...t, open: false } : t
        )
      };
    }
    case "REMOVE_TOAST":
      if (action.toastId === void 0) return { ...state, toasts: [] };
      return { ...state, toasts: state.toasts.filter((t) => t.id !== action.toastId) };
  }
}
var listeners = [];
var memoryState = { toasts: [] };
function dispatch(action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => listener(memoryState));
}
function toast(options) {
  const id = genId();
  const update = (next) => dispatch({ type: "UPDATE_TOAST", toast: { ...next, id } });
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });
  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...options,
      id,
      open: true,
      onOpenChange: (open) => {
        options.onOpenChange?.(open);
        if (!open) dismiss();
      }
    }
  });
  return { id, dismiss, update };
}
function useToast() {
  const [state, setState] = React9.useState(memoryState);
  React9.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) listeners.splice(index, 1);
    };
  }, []);
  return {
    ...state,
    toast,
    dismiss: (toastId) => dispatch({ type: "DISMISS_TOAST", toastId })
  };
}
function dismissAllToasts() {
  dispatch({ type: "DISMISS_TOAST" });
}
var toastVariants = cva3(
  cn(
    // A toast floats, so it takes the one overlay surface. The variants add
    // a 2px rule on the left edge and nothing else: no fill, no second box.
    OVERLAY_SURFACE,
    "group relative flex w-full items-start gap-3 p-4 pr-12 text-text",
    "transition-[opacity,translate] duration-base ease-move",
    "data-[state=closed]:opacity-0 data-[state=open]:opacity-100",
    // Radix writes the swipe offset to custom properties; reading them is the
    // one legitimate use of the bracket syntax.
    "data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]",
    "data-[swipe=move]:translate-y-[var(--radix-toast-swipe-move-y)]",
    "data-[swipe=move]:transition-none",
    "data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:translate-y-0",
    "data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]",
    "data-[swipe=end]:translate-y-[var(--radix-toast-swipe-end-y)]",
    "data-[swipe=end]:opacity-0",
    // Radix makes the toast itself focusable (F8, then Tab), so it needs the
    // ring like any other focus stop.
    FOCUS_RING
  ),
  {
    variants: {
      variant: {
        default: "",
        success: "border-l-2 border-l-status-compliant",
        warning: "border-l-2 border-l-status-warning",
        destructive: "border-l-2 border-l-status-breach"
      }
    },
    defaultVariants: { variant: "default" }
  }
);
var SEVERITY = {
  success: { Glyph: CircleCheck, word: "Success", tone: "text-status-compliant" },
  warning: { Glyph: TriangleAlert, word: "Warning", tone: "text-status-warning" },
  destructive: { Glyph: CircleAlert2, word: "Error", tone: "text-status-breach" }
};
var Toast = React9.forwardRef(
  ({ className, variant, title, description, action, children, ...props }, ref) => {
    const generated = React9.useId();
    const wordId = `toast-${generated}-severity`;
    const titleId = `toast-${generated}-title`;
    const descriptionId = `toast-${generated}-description`;
    const severity = variant && variant !== "default" ? SEVERITY[variant] : null;
    return /* @__PURE__ */ jsxs7(
      ToastPrimitive.Root,
      {
        ref,
        "aria-labelledby": title ? [severity ? wordId : null, titleId].filter(Boolean).join(" ") : void 0,
        "aria-describedby": description ? descriptionId : void 0,
        "data-variant": variant ?? "default",
        className: cn(toastVariants({ variant }), className),
        ...props,
        children: [
          severity ? /* @__PURE__ */ jsxs7(Fragment3, { children: [
            /* @__PURE__ */ jsx9(
              severity.Glyph,
              {
                className: cn("mt-px size-4 shrink-0", severity.tone),
                "aria-hidden": "true"
              }
            ),
            /* @__PURE__ */ jsxs7("span", { id: wordId, className: "sr-only", children: [
              severity.word,
              ":",
              " "
            ] })
          ] }) : null,
          /* @__PURE__ */ jsxs7("div", { className: "flex min-w-0 flex-1 flex-col gap-1", children: [
            title ? /* @__PURE__ */ jsx9(ToastPrimitive.Title, { id: titleId, className: "text-body font-medium text-text", children: title }) : null,
            " ",
            description ? /* @__PURE__ */ jsx9(
              ToastPrimitive.Description,
              {
                id: descriptionId,
                className: "text-body-sm text-text-muted",
                children: description
              }
            ) : null,
            " ",
            children
          ] }),
          action ? /* @__PURE__ */ jsx9("div", { className: "shrink-0 self-center", children: action }) : null,
          /* @__PURE__ */ jsx9(ToastPrimitive.Close, { asChild: true, children: /* @__PURE__ */ jsx9(
            Button,
            {
              variant: "ghost",
              size: "icon",
              "aria-label": "Dismiss",
              className: "absolute right-2 top-2 text-text-muted hover:text-text",
              children: /* @__PURE__ */ jsx9(X, { "aria-hidden": "true" })
            }
          ) })
        ]
      }
    );
  }
);
Toast.displayName = "Toast";
var ToastAction = React9.forwardRef(
  ({ altText, variant = "ghost", size = "sm", className, ...props }, ref) => /* @__PURE__ */ jsx9(ToastPrimitive.Action, { asChild: true, altText, children: /* @__PURE__ */ jsx9(Button, { ref, variant, size, className: cn("shrink-0", className), ...props }) })
);
ToastAction.displayName = "ToastAction";
var POSITIONS = {
  // Newest first in the queue, so bottom anchors reverse the column: the
  // newest toast lands nearest the edge and pushes older ones inward.
  "top-left": "left-0 top-0 flex-col",
  "top-center": "left-1/2 top-0 -translate-x-1/2 flex-col",
  "top-right": "right-0 top-0 flex-col",
  "bottom-left": "bottom-0 left-0 flex-col-reverse",
  "bottom-center": "bottom-0 left-1/2 -translate-x-1/2 flex-col-reverse",
  "bottom-right": "bottom-0 right-0 flex-col-reverse"
};
var SWIPE_FOR = {
  "top-left": "left",
  "top-center": "up",
  "top-right": "right",
  "bottom-left": "left",
  "bottom-center": "down",
  "bottom-right": "right"
};
var Toaster = React9.forwardRef(
  ({ position = "bottom-right", duration = 6e3, swipeDirection, className, ...viewportProps }, ref) => {
    const { toasts } = useToast();
    return /* @__PURE__ */ jsxs7(ToastPrimitive.Provider, { duration, swipeDirection: swipeDirection ?? SWIPE_FOR[position], children: [
      toasts.map(({ id, ...props }) => /* @__PURE__ */ jsx9(Toast, { ...props }, id)),
      /* @__PURE__ */ jsx9(
        ToastPrimitive.Viewport,
        {
          ref,
          className: cn(
            "fixed z-50 flex max-h-screen w-full list-none gap-3 p-4 outline-none sm:max-w-md",
            POSITIONS[position],
            className
          ),
          ...viewportProps
        }
      )
    ] });
  }
);
Toaster.displayName = "Toaster";

// src/components/dialog.tsx
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X as X2 } from "lucide-react";
import * as React11 from "react";

// src/components/stepper.tsx
import { Check as Check3 } from "lucide-react";
import * as React10 from "react";
import { Fragment as Fragment4, jsx as jsx10, jsxs as jsxs8 } from "react/jsx-runtime";
var STATE_WORD = {
  complete: "Complete",
  current: "Current",
  incomplete: "Incomplete"
};
var KEY_ORDER = ["complete", "current", "incomplete"];
var MARKER_TONE = {
  complete: "border-text-muted bg-text-muted text-text-inverse",
  current: "border-accent bg-accent text-accent-foreground",
  incomplete: "border-control-border text-text-muted"
};
function isStage(item) {
  return Array.isArray(item.steps);
}
function normalise(items) {
  const stages = [];
  let run = null;
  let index = 0;
  for (const item of items) {
    if (isStage(item)) {
      run = null;
      stages.push({
        key: item.id,
        label: item.label,
        steps: item.steps.map((step) => ({ ...step, index: index++, stage: item.label }))
      });
      continue;
    }
    if (!run) {
      run = { key: `ungrouped-${stages.length}`, steps: [] };
      stages.push(run);
    }
    run.steps.push({ ...item, index: index++ });
  }
  return stages;
}
function flattenSteps(items) {
  return normalise(items).flatMap((stage) => stage.steps);
}
function Marker({
  state,
  number,
  size = "md"
}) {
  return /* @__PURE__ */ jsx10(
    "span",
    {
      "aria-hidden": "true",
      className: cn(
        "inline-flex shrink-0 items-center justify-center rounded-pill border font-mono text-caption",
        size === "md" ? "size-5" : "size-4",
        MARKER_TONE[state]
      ),
      children: state === "complete" ? /* @__PURE__ */ jsx10(Check3, { className: size === "md" ? "size-3" : "size-2.5" }) : number
    }
  );
}
var Stepper = React10.forwardRef(
  ({
    steps,
    currentStep,
    completedSteps,
    onStepSelect,
    orientation = "horizontal",
    showKey = true,
    showCount = true,
    idPrefix,
    className,
    "aria-label": ariaLabel = "Progress",
    ...props
  }, ref) => {
    const generated = React10.useId();
    const prefix = idPrefix ?? `stepper-${generated}`;
    const stages = React10.useMemo(() => normalise(steps), [steps]);
    const completed = React10.useMemo(() => new Set(completedSteps ?? []), [completedSteps]);
    const total = stages.reduce((sum, stage) => sum + stage.steps.length, 0);
    const position = total === 0 ? 0 : Math.min(Math.max(currentStep, 0), total - 1) + 1;
    const horizontal = orientation === "horizontal";
    const flat = stages.length === 1 && stages[0].label === void 0;
    const renderStep = (step) => {
      const state = step.index === currentStep ? "current" : step.index < currentStep || completed.has(step.index) ? "complete" : "incomplete";
      const navigable = Boolean(onStepSelect) && state !== "incomplete";
      const current = state === "current" ? "step" : void 0;
      const body = /* @__PURE__ */ jsxs8(Fragment4, { children: [
        /* @__PURE__ */ jsx10(Marker, { state, number: step.index + 1 }),
        /* @__PURE__ */ jsxs8("span", { children: [
          step.label,
          step.optional ? /* @__PURE__ */ jsx10("span", { className: "text-text-muted", children: " (optional)" }) : null
        ] }),
        /* @__PURE__ */ jsx10("span", { className: "sr-only", children: `, ${STATE_WORD[state]}` })
      ] });
      return /* @__PURE__ */ jsx10(
        "li",
        {
          id: `${prefix}-${step.id}`,
          "data-state": state,
          "aria-current": navigable ? void 0 : current,
          className: cn(
            "min-w-0 text-body-sm",
            state === "current" ? "font-medium text-text" : "text-text-muted"
          ),
          children: navigable ? /* @__PURE__ */ jsx10(
            "button",
            {
              type: "button",
              "aria-current": current,
              onClick: () => onStepSelect?.(step.index),
              className: cn(
                "flex items-center gap-2 rounded-sm text-left",
                "transition-colors duration-fast ease-move hover:text-text",
                FOCUS_RING
              ),
              children: body
            }
          ) : /* @__PURE__ */ jsx10("span", { className: "flex items-center gap-2", children: body })
        },
        step.id
      );
    };
    const stepList = (stage, labelledBy) => /* @__PURE__ */ jsx10(
      "ol",
      {
        "aria-labelledby": labelledBy,
        className: cn(horizontal ? "flex flex-wrap gap-x-5 gap-y-2" : "flex flex-col gap-2"),
        children: stage.steps.map(renderStep)
      }
    );
    return /* @__PURE__ */ jsxs8(
      "nav",
      {
        ref,
        "aria-label": ariaLabel,
        className: cn(
          "flex min-w-0 flex-col gap-3",
          // The hairline is the whole separation between the stepper and the
          // body under it -- section 12's structure, with nothing enclosed.
          horizontal && "border-b border-divider pb-3",
          className
        ),
        ...props,
        children: [
          /* @__PURE__ */ jsxs8(
            "div",
            {
              className: cn(
                "flex min-w-0 gap-x-6 gap-y-2",
                horizontal ? "flex-wrap items-start justify-between" : "flex-col"
              ),
              children: [
                flat ? stepList(stages[0]) : /* @__PURE__ */ jsx10("ol", { className: cn(horizontal ? "flex flex-wrap gap-x-8 gap-y-4" : "flex flex-col gap-4"), children: stages.map((stage) => {
                  const eyebrowId = `${prefix}-stage-${stage.key}`;
                  return /* @__PURE__ */ jsxs8("li", { className: "flex min-w-0 flex-col gap-2", children: [
                    stage.label !== void 0 ? /* @__PURE__ */ jsx10("p", { id: eyebrowId, className: "eyebrow", children: stage.label }) : null,
                    stepList(stage, stage.label !== void 0 ? eyebrowId : void 0)
                  ] }, stage.key);
                }) }),
                showCount && total > 0 ? /* @__PURE__ */ jsxs8("p", { className: "shrink-0 font-mono text-caption text-text-muted", "data-numeric": true, children: [
                  "Step ",
                  position,
                  " of ",
                  total
                ] }) : null
              ]
            }
          ),
          showKey ? /* @__PURE__ */ jsxs8("p", { className: "flex flex-wrap items-center gap-x-4 gap-y-1 text-caption text-text-muted", children: [
            /* @__PURE__ */ jsx10("span", { className: "sr-only", children: "Step status key: " }),
            KEY_ORDER.map((state) => /* @__PURE__ */ jsxs8("span", { className: "inline-flex items-center gap-1.5", children: [
              /* @__PURE__ */ jsx10(Marker, { state, size: "sm" }),
              STATE_WORD[state]
            ] }, state))
          ] }) : null
        ]
      }
    );
  }
);
Stepper.displayName = "Stepper";

// src/components/dialog.tsx
import { Fragment as Fragment5, jsx as jsx11, jsxs as jsxs9 } from "react/jsx-runtime";
var Dialog = DialogPrimitive.Root;
var DialogTrigger = DialogPrimitive.Trigger;
var DialogClose = DialogPrimitive.Close;
var DialogPortal = DialogPrimitive.Portal;
var SIZES = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl"
};
function focusFirstField(root, preventScroll = false) {
  const first = root?.querySelector(
    [
      "input:not([type=hidden]):not([disabled])",
      "textarea:not([disabled])",
      "select:not([disabled])",
      // The roles, not the tags: every control in this package that is not a
      // native input renders as a button carrying its role -- Checkbox and
      // RadioGroupItem are `role=checkbox` / `role=radio`, Select's trigger is
      // `role=combobox`. Matching tags alone walked straight past a section
      // whose first field was a checkbox and landed on the one after it,
      // which is the deep link missing by one.
      "[role=combobox]:not([disabled])",
      "[role=checkbox]:not([disabled])",
      "[role=radio]:not([disabled])",
      "[role=switch]:not([disabled])"
    ].map((selector) => `${selector}:not([aria-hidden]):not([tabindex="-1"])`).join(", ")
  );
  if (!first) return false;
  first.focus({ preventScroll });
  return document.activeElement === first;
}
function prefersReducedMotion() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
function clampStep(step, count2) {
  if (count2 <= 0) return 0;
  return Math.min(Math.max(Math.trunc(step), 0), count2 - 1);
}
function revealSection(root, id) {
  let section = null;
  try {
    section = root?.querySelector(`[id="${id.replace(/["\\]/g, "\\$&")}"]`) ?? null;
  } catch {
    section = null;
  }
  if (!section) return false;
  if (!focusFirstField(section, true)) {
    const focusable = section.matches(
      "a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]"
    );
    if (!focusable) {
      section.tabIndex = -1;
      section.addEventListener("blur", () => section.removeAttribute("tabindex"), { once: true });
    }
    section.focus({ preventScroll: true });
    if (document.activeElement !== section) return false;
  }
  section.scrollIntoView({ block: "start", behavior: prefersReducedMotion() ? "auto" : "smooth" });
  return true;
}
var DialogContent = React11.forwardRef(
  ({
    className,
    children,
    title,
    description,
    size = "md",
    footer,
    dismissOnOutside = true,
    closable = true,
    autoFocusFirstField = true,
    onOpenAutoFocus,
    onCloseAutoFocus,
    onInteractOutside,
    ...props
  }, ref) => {
    const inner = React11.useRef(null);
    const opener = React11.useRef(null);
    return /* @__PURE__ */ jsxs9(DialogPrimitive.Portal, { children: [
      /* @__PURE__ */ jsx11(
        DialogPrimitive.Overlay,
        {
          className: cn(
            "fixed inset-0 z-50 bg-surface-nav-deep/60",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "transition-opacity duration-base ease-move",
            "data-[state=closed]:opacity-0 data-[state=open]:opacity-100"
          )
        }
      ),
      /* @__PURE__ */ jsxs9(
        DialogPrimitive.Content,
        {
          ref: (el) => {
            inner.current = el;
            if (typeof ref === "function") ref(el);
            else if (ref) ref.current = el;
          },
          onOpenAutoFocus: (event) => {
            opener.current = document.activeElement;
            onOpenAutoFocus?.(event);
            if (event.defaultPrevented) return;
            if (autoFocusFirstField && focusFirstField(inner.current)) event.preventDefault();
          },
          onCloseAutoFocus: (event) => {
            onCloseAutoFocus?.(event);
            const target = opener.current;
            opener.current = null;
            if (event.defaultPrevented) return;
            if (target && target.isConnected && target !== document.body) {
              event.preventDefault();
              target.focus();
            }
          },
          onInteractOutside: (event) => {
            onInteractOutside?.(event);
            if (!dismissOnOutside) event.preventDefault();
          },
          className: cn(
            "fixed left-1/2 top-1/2 z-50 flex w-full -translate-x-1/2 -translate-y-1/2 flex-col",
            "max-h-[calc(100vh-3rem)] rounded-lg border border-divider bg-surface-raised shadow-dialog",
            "text-text outline-none",
            "transition-[opacity,transform] duration-base ease-move",
            "data-[state=closed]:opacity-0 data-[state=open]:opacity-100",
            SIZES[size],
            className
          ),
          ...props,
          children: [
            /* @__PURE__ */ jsxs9("div", { className: "flex items-start justify-between gap-4 px-6 pt-6", children: [
              /* @__PURE__ */ jsxs9("div", { className: "flex min-w-0 flex-col gap-1", children: [
                /* @__PURE__ */ jsx11(DialogPrimitive.Title, { className: "m-0 text-h2 text-text", children: title }),
                description ? /* @__PURE__ */ jsx11(DialogPrimitive.Description, { className: "text-body-sm text-text-muted", children: description }) : (
                  // Radix warns without one; an empty description is honest
                  // where the title says everything.
                  /* @__PURE__ */ jsx11(DialogPrimitive.Description, { className: "sr-only", children: title })
                )
              ] }),
              closable ? /* @__PURE__ */ jsx11(DialogPrimitive.Close, { asChild: true, children: /* @__PURE__ */ jsx11(
                "button",
                {
                  type: "button",
                  "aria-label": "Close",
                  className: cn(
                    "inline-flex size-8 shrink-0 items-center justify-center rounded-sm text-text-muted",
                    "hover:bg-surface-sunken hover:text-text transition-colors duration-fast ease-move",
                    FOCUS_RING
                  ),
                  children: /* @__PURE__ */ jsx11(X2, { className: "size-4", "aria-hidden": "true" })
                }
              ) }) : null
            ] }),
            /* @__PURE__ */ jsx11("div", { className: "min-h-0 flex-1 overflow-y-auto px-6 py-6", children }),
            footer ? /* @__PURE__ */ jsx11("div", { className: "flex items-center justify-end gap-3 border-t border-divider px-6 py-4", children: footer }) : null
          ]
        }
      )
    ] });
  }
);
DialogContent.displayName = "DialogContent";
function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  size = "lg",
  initialSection,
  onSubmit,
  submitLabel = "Save",
  submittingLabel = "Saving\u2026",
  submitting = false,
  canSubmit = true,
  cancelLabel = "Cancel",
  secondaryAction,
  children,
  className
}) {
  const formId = React11.useId();
  const content = React11.useRef(null);
  return /* @__PURE__ */ jsx11(Dialog, { open, onOpenChange: (next) => submitting && !next ? void 0 : onOpenChange(next), children: /* @__PURE__ */ jsx11(
    DialogContent,
    {
      ref: content,
      title,
      description,
      size,
      dismissOnOutside: false,
      className,
      onOpenAutoFocus: (event) => {
        if (!initialSection) return;
        if (revealSection(content.current, initialSection)) event.preventDefault();
      },
      onEscapeKeyDown: (e) => {
        if (submitting) e.preventDefault();
      },
      footer: /* @__PURE__ */ jsxs9(Fragment5, { children: [
        secondaryAction ? /* @__PURE__ */ jsx11("div", { className: "mr-auto", children: secondaryAction }) : null,
        /* @__PURE__ */ jsx11(Button, { type: "button", variant: "secondary", onClick: () => onOpenChange(false), disabled: submitting, children: cancelLabel }),
        /* @__PURE__ */ jsx11(Button, { type: "submit", form: formId, pending: submitting, disabled: !canSubmit, children: submitting ? submittingLabel : submitLabel })
      ] }),
      children: /* @__PURE__ */ jsx11(
        "form",
        {
          id: formId,
          noValidate: true,
          onSubmit: (event) => {
            event.preventDefault();
            onSubmit(event);
          },
          className: "flex flex-col gap-6",
          children
        }
      )
    }
  ) });
}
function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  verb,
  destructive = false,
  onConfirm,
  pending = false,
  pendingLabel,
  cancelLabel = "Cancel",
  confirmText,
  children
}) {
  const [typed, setTyped] = React11.useState("");
  const cancelRef = React11.useRef(null);
  const armed = !confirmText || typed === confirmText;
  React11.useEffect(() => {
    if (!open) setTyped("");
  }, [open]);
  return /* @__PURE__ */ jsx11(Dialog, { open, onOpenChange: (next) => pending && !next ? void 0 : onOpenChange(next), children: /* @__PURE__ */ jsxs9(
    DialogContent,
    {
      role: "alertdialog",
      title,
      description,
      size: "sm",
      dismissOnOutside: false,
      closable: false,
      autoFocusFirstField: Boolean(confirmText),
      onOpenAutoFocus: (event) => {
        if (!confirmText && destructive) {
          event.preventDefault();
          cancelRef.current?.focus();
        }
      },
      onEscapeKeyDown: (e) => {
        if (pending) e.preventDefault();
      },
      footer: /* @__PURE__ */ jsxs9(Fragment5, { children: [
        /* @__PURE__ */ jsx11(
          Button,
          {
            ref: cancelRef,
            type: "button",
            variant: "secondary",
            onClick: () => onOpenChange(false),
            disabled: pending,
            children: cancelLabel
          }
        ),
        /* @__PURE__ */ jsx11(
          Button,
          {
            type: "button",
            variant: destructive ? "destructive" : "primary",
            onClick: onConfirm,
            pending,
            disabled: !armed,
            children: pending && pendingLabel ? pendingLabel : verb
          }
        )
      ] }),
      children: [
        children ? /* @__PURE__ */ jsx11("div", { className: "flex flex-col gap-4 text-body text-text", children }) : null,
        confirmText ? /* @__PURE__ */ jsx11("div", { className: cn(children && "mt-6"), children: /* @__PURE__ */ jsx11(
          Field,
          {
            label: /* @__PURE__ */ jsxs9(Fragment5, { children: [
              "Type ",
              /* @__PURE__ */ jsx11("span", { className: "font-mono", children: confirmText }),
              " to confirm"
            ] }),
            helpLabel: "About typed confirmation",
            children: /* @__PURE__ */ jsx11(
              Input,
              {
                value: typed,
                onChange: (e) => setTyped(e.target.value),
                autoComplete: "off",
                spellCheck: false
              }
            )
          }
        ) }) : null
      ]
    }
  ) });
}
function WizardDialog({
  open,
  onOpenChange,
  title,
  description,
  steps,
  currentStep,
  initialStep,
  onOpenAtStep,
  onNext,
  onBack,
  onSubmit,
  onSaveDraft,
  submitLabel = "Submit",
  submittingLabel = "Submitting\u2026",
  submitting = false,
  canProceed = true,
  onStepSelect,
  completedSteps,
  submitFromAnyStep = false,
  size = "xl",
  children
}) {
  const handedOver = React11.useRef(null);
  React11.useLayoutEffect(() => {
    if (!open) {
      handedOver.current = null;
      return;
    }
    if (initialStep === void 0 || !Number.isFinite(initialStep)) return;
    if (steps.length === 0) return;
    const key = `${initialStep}:${steps.length}`;
    if (handedOver.current === key) return;
    handedOver.current = key;
    onOpenAtStep?.(clampStep(initialStep, steps.length));
  }, [open, initialStep, onOpenAtStep, steps.length]);
  const last = currentStep === steps.length - 1;
  const step = steps[currentStep];
  return /* @__PURE__ */ jsx11(Dialog, { open, onOpenChange: (next) => submitting && !next ? void 0 : onOpenChange(next), children: /* @__PURE__ */ jsxs9(
    DialogContent,
    {
      title,
      description,
      size,
      dismissOnOutside: false,
      onEscapeKeyDown: (e) => {
        if (submitting) e.preventDefault();
      },
      footer: /* @__PURE__ */ jsxs9(Fragment5, { children: [
        /* @__PURE__ */ jsxs9("div", { className: "mr-auto flex items-center gap-4", children: [
          /* @__PURE__ */ jsx11(Button, { type: "button", variant: "ghost", onClick: onSaveDraft, disabled: submitting, children: "Save draft" }),
          /* @__PURE__ */ jsxs9("span", { className: "font-mono text-caption text-text-muted", "data-numeric": true, children: [
            "Step ",
            currentStep + 1,
            " of ",
            steps.length
          ] })
        ] }),
        /* @__PURE__ */ jsx11(
          Button,
          {
            type: "button",
            variant: "secondary",
            onClick: onBack,
            disabled: currentStep === 0 || submitting,
            children: "Back"
          }
        ),
        !last ? /* @__PURE__ */ jsxs9(Fragment5, { children: [
          submitFromAnyStep ? /* @__PURE__ */ jsx11(
            Button,
            {
              type: "button",
              variant: "secondary",
              onClick: onSubmit,
              pending: submitting,
              disabled: !canProceed,
              children: submitting ? submittingLabel : submitLabel
            }
          ) : null,
          /* @__PURE__ */ jsx11(Button, { type: "button", onClick: onNext, disabled: !canProceed || submitting, children: "Next" })
        ] }) : /* @__PURE__ */ jsx11(Button, { type: "button", onClick: onSubmit, pending: submitting, disabled: !canProceed, children: submitting ? submittingLabel : submitLabel })
      ] }),
      children: [
        /* @__PURE__ */ jsx11(
          Stepper,
          {
            steps,
            currentStep,
            onStepSelect,
            completedSteps,
            idPrefix: "wizard-step",
            "aria-label": "Steps",
            showCount: false
          }
        ),
        /* @__PURE__ */ jsx11("div", { className: "mt-6 flex flex-col gap-6", "aria-labelledby": `wizard-step-${step?.id}`, children })
      ]
    }
  ) });
}

// src/components/select.tsx
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check as Check4, ChevronDown, ChevronUp } from "lucide-react";
import * as React12 from "react";
import { jsx as jsx12, jsxs as jsxs10 } from "react/jsx-runtime";
var Select = SelectPrimitive.Root;
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
var SelectTrigger = React12.forwardRef(({ className, children, invalid, ...props }, ref) => {
  const field = useFieldContext();
  const wired = useFieldControl(props);
  const labelledBy = props["aria-labelledby"] ?? field?.labelId;
  const isInvalid = invalid ?? Boolean(wired["aria-invalid"]);
  if (process.env.NODE_ENV !== "production") {
    if (!props["aria-label"] && !labelledBy) {
      console.warn(
        "[@profylr/ui] SelectTrigger has no accessible name. A combobox is never named by its placeholder or by <label for>. Wrap it in a Field, or pass aria-label / aria-labelledby."
      );
    }
  }
  return /* @__PURE__ */ jsxs10(
    SelectPrimitive.Trigger,
    {
      ref,
      className: cn(
        // control-surface, not surface -- see Input for the two themes' logics.
        "flex h-control w-full items-center justify-between gap-2 rounded-sm border bg-control-surface px-6",
        "focus:bg-control-surface-focus data-[state=open]:bg-control-surface-focus",
        "text-body text-text transition-colors duration-fast ease-move",
        "data-[placeholder]:text-text-muted",
        "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-70",
        "[&>span]:line-clamp-1 [&>span]:text-left",
        isInvalid ? "border-status-breach" : "border-control-border",
        FOCUS_RING,
        className
      ),
      ...wired,
      "aria-labelledby": labelledBy,
      "aria-invalid": isInvalid || void 0,
      children: [
        children,
        /* @__PURE__ */ jsx12(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx12(ChevronDown, { className: "size-4 shrink-0 text-text-muted", "aria-hidden": "true" }) })
      ]
    }
  );
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
var SelectScrollUpButton = React12.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx12(
  SelectPrimitive.ScrollUpButton,
  {
    ref,
    className: cn("flex cursor-default items-center justify-center py-1", className),
    ...props,
    children: /* @__PURE__ */ jsx12(ChevronUp, { className: "size-4", "aria-hidden": "true" })
  }
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
var SelectScrollDownButton = React12.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx12(
  SelectPrimitive.ScrollDownButton,
  {
    ref,
    className: cn("flex cursor-default items-center justify-center py-1", className),
    ...props,
    children: /* @__PURE__ */ jsx12(ChevronDown, { className: "size-4", "aria-hidden": "true" })
  }
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;
var SelectContent = React12.forwardRef(({ className, children, position = "popper", ...props }, ref) => /* @__PURE__ */ jsx12(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs10(
  SelectPrimitive.Content,
  {
    ref,
    position,
    className: cn(
      OVERLAY_SURFACE,
      "relative max-h-96 min-w-32 overflow-hidden text-text",
      "transition-[opacity,translate] duration-fast ease-move",
      "data-[state=closed]:opacity-0 data-[state=open]:opacity-100",
      "data-[state=closed]:-translate-y-1 data-[state=open]:translate-y-0",
      position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx12(SelectScrollUpButton, {}),
      /* @__PURE__ */ jsx12(
        SelectPrimitive.Viewport,
        {
          className: cn(
            "p-1",
            position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
          ),
          children
        }
      ),
      /* @__PURE__ */ jsx12(SelectScrollDownButton, {})
    ]
  }
) }));
SelectContent.displayName = SelectPrimitive.Content.displayName;
var SelectLabel = React12.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx12(
  SelectPrimitive.Label,
  {
    ref,
    className: cn("micro-label py-2 pl-8 pr-6", className),
    ...props
  }
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;
var SelectItem = React12.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs10(
  SelectPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-3 pl-8 pr-6",
      "text-body text-text outline-none",
      "data-[highlighted]:bg-surface-sunken",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx12("span", { className: "absolute left-2 flex size-4 items-center justify-center", children: /* @__PURE__ */ jsx12(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx12(Check4, { className: "size-4 text-action", "aria-hidden": "true" }) }) }),
      /* @__PURE__ */ jsx12(SelectPrimitive.ItemText, { children })
    ]
  }
));
SelectItem.displayName = SelectPrimitive.Item.displayName;
var SelectSeparator = React12.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx12(
  SelectPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-divider", className),
    ...props
  }
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

// src/components/dropdown-menu.tsx
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check as Check5, ChevronRight, Circle } from "lucide-react";
import * as React13 from "react";
import { jsx as jsx13, jsxs as jsxs11 } from "react/jsx-runtime";
var DropdownMenu = DropdownMenuPrimitive.Root;
var DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
var DropdownMenuGroup = DropdownMenuPrimitive.Group;
var DropdownMenuPortal = DropdownMenuPrimitive.Portal;
var DropdownMenuSub = DropdownMenuPrimitive.Sub;
var DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
var itemBase = cn(
  "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5",
  "text-body text-text outline-none transition-colors duration-instant",
  "data-[highlighted]:bg-surface-sunken",
  "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
  "[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:pointer-events-none"
);
var DropdownMenuSubTrigger = React13.forwardRef(({ className, inset, children, ...props }, ref) => /* @__PURE__ */ jsxs11(
  DropdownMenuPrimitive.SubTrigger,
  {
    ref,
    className: cn(itemBase, "data-[state=open]:bg-surface-sunken", inset && "pl-8", className),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx13(ChevronRight, { className: "ml-auto text-text-muted", "aria-hidden": "true" })
    ]
  }
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
var DropdownMenuSubContent = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx13(
  DropdownMenuPrimitive.SubContent,
  {
    ref,
    className: cn(OVERLAY_SURFACE, "min-w-32 overflow-hidden p-1", className),
    ...props
  }
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
var DropdownMenuContent = React13.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx13(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx13(
  DropdownMenuPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      OVERLAY_SURFACE,
      "min-w-32 overflow-hidden p-1 text-text",
      /*
       * Driven from the motion tokens rather than tailwindcss-animate, which
       * is not a dependency -- animate-in and fade-in-0 compiled to nothing,
       * so every overlay simply appeared.
       */
      "transition-[opacity,translate] duration-fast ease-move",
      "data-[state=closed]:opacity-0 data-[state=open]:opacity-100",
      "data-[state=closed]:-translate-y-1 data-[state=open]:translate-y-0",
      className
    ),
    ...props
  }
) }));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
var DropdownMenuItem = React13.forwardRef(({ className, inset, destructive, ...props }, ref) => /* @__PURE__ */ jsx13(
  DropdownMenuPrimitive.Item,
  {
    ref,
    className: cn(
      itemBase,
      inset && "pl-8",
      destructive && "text-status-breach data-[highlighted]:bg-status-breach-bg",
      className
    ),
    ...props
  }
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
var DropdownMenuCheckboxItem = React13.forwardRef(({ className, children, checked, ...props }, ref) => /* @__PURE__ */ jsxs11(
  DropdownMenuPrimitive.CheckboxItem,
  {
    ref,
    checked,
    className: cn(itemBase, "pl-8", className),
    ...props,
    children: [
      /* @__PURE__ */ jsx13("span", { className: "absolute left-2 flex size-4 items-center justify-center", children: /* @__PURE__ */ jsx13(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx13(Check5, { className: "size-4 text-action", "aria-hidden": "true" }) }) }),
      children
    ]
  }
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;
var DropdownMenuRadioItem = React13.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs11(
  DropdownMenuPrimitive.RadioItem,
  {
    ref,
    className: cn(itemBase, "pl-8", className),
    ...props,
    children: [
      /* @__PURE__ */ jsx13("span", { className: "absolute left-2 flex size-4 items-center justify-center", children: /* @__PURE__ */ jsx13(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx13(Circle, { className: "size-2 fill-action text-action", "aria-hidden": "true" }) }) }),
      children
    ]
  }
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
var DropdownMenuLabel = React13.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx13(
  DropdownMenuPrimitive.Label,
  {
    ref,
    className: cn("micro-label px-2 py-1.5", inset && "pl-8", className),
    ...props
  }
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
var DropdownMenuSeparator = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx13(
  DropdownMenuPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-divider", className),
    ...props
  }
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
function DropdownMenuShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx13(
    "span",
    {
      className: cn("ml-auto font-mono text-caption text-text-muted", className),
      ...props
    }
  );
}
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

// src/components/tooltip.tsx
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React14 from "react";
import { jsx as jsx14, jsxs as jsxs12 } from "react/jsx-runtime";
var TooltipProvider = TooltipPrimitive.Provider;
var Tooltip = TooltipPrimitive.Root;
var TooltipTrigger = TooltipPrimitive.Trigger;
var TooltipPortal = TooltipPrimitive.Portal;
var TooltipContent = React14.forwardRef(({ className, sideOffset = 6, children, ...props }, ref) => /* @__PURE__ */ jsx14(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsxs12(
  TooltipPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      OVERLAY_SURFACE,
      "max-w-64 px-2.5 py-1.5 text-body-small text-text",
      "transition-[opacity,translate] duration-fast ease-move",
      "data-[state=closed]:opacity-0 data-[state=delayed-open]:opacity-100",
      "data-[state=closed]:-translate-y-1 data-[state=delayed-open]:translate-y-0",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx14(TooltipPrimitive.Arrow, { className: "fill-surface-raised", width: 10, height: 5 })
    ]
  }
) }));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// src/components/hover-card.tsx
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
import * as React15 from "react";
import { jsx as jsx15 } from "react/jsx-runtime";
var HoverCard = HoverCardPrimitive.Root;
var HoverCardTrigger = HoverCardPrimitive.Trigger;
var HoverCardPortal = HoverCardPrimitive.Portal;
var HoverCardContent = React15.forwardRef(({ className, align = "center", sideOffset = 6, ...props }, ref) => /* @__PURE__ */ jsx15(HoverCardPrimitive.Portal, { children: /* @__PURE__ */ jsx15(
  HoverCardPrimitive.Content,
  {
    ref,
    align,
    sideOffset,
    className: cn(
      OVERLAY_SURFACE,
      "w-64 p-4 text-body text-text",
      "transition-[opacity,translate] duration-fast ease-move",
      "data-[state=closed]:opacity-0 data-[state=open]:opacity-100",
      "data-[state=closed]:-translate-y-1 data-[state=open]:translate-y-0",
      className
    ),
    ...props
  }
) }));
HoverCardContent.displayName = HoverCardPrimitive.Content.displayName;

// src/components/scroll-area.tsx
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import * as React16 from "react";
import { jsx as jsx16, jsxs as jsxs13 } from "react/jsx-runtime";
var ScrollArea = React16.forwardRef(({ className, children, orientation = "vertical", ...props }, ref) => /* @__PURE__ */ jsxs13(
  ScrollAreaPrimitive.Root,
  {
    ref,
    className: cn("relative overflow-hidden", className),
    ...props,
    children: [
      /* @__PURE__ */ jsx16(
        ScrollAreaPrimitive.Viewport,
        {
          tabIndex: 0,
          className: "size-full rounded-[inherit] focus-ring outline-none",
          children
        }
      ),
      (orientation === "vertical" || orientation === "both") && /* @__PURE__ */ jsx16(ScrollBar, { orientation: "vertical" }),
      (orientation === "horizontal" || orientation === "both") && /* @__PURE__ */ jsx16(ScrollBar, { orientation: "horizontal" }),
      /* @__PURE__ */ jsx16(ScrollAreaPrimitive.Corner, {})
    ]
  }
));
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;
var ScrollBar = React16.forwardRef(({ className, orientation = "vertical", ...props }, ref) => /* @__PURE__ */ jsx16(
  ScrollAreaPrimitive.ScrollAreaScrollbar,
  {
    ref,
    orientation,
    className: cn(
      "flex touch-none select-none transition-colors duration-fast",
      orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent p-px",
      orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent p-px",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx16(ScrollAreaPrimitive.ScrollAreaThumb, { className: "relative flex-1 rounded-pill bg-control-border" })
  }
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

// src/components/app-header.tsx
import { jsx as jsx17, jsxs as jsxs14 } from "react/jsx-runtime";
function AppHeader({
  identity,
  product,
  productMark,
  actions,
  onMenuToggle,
  className
}) {
  return /* @__PURE__ */ jsxs14(
    "header",
    {
      className: cn(
        "relative flex h-22 shrink-0 items-center justify-between gap-4",
        /*
         * `surface-header`, not `surface`. In light the two are the same white
         * and the bar reads as the canvas continuing upward; in dark the token
         * drops to the rail's value, so the rail and the bar frame the content
         * instead of meeting it as a third panel.
         */
        "border-b border-divider bg-surface-header px-5 sm:px-8",
        /*
         * Clips the product watermark, which is deliberately larger than the
         * bar and bleeds past its top and right edges.
         *
         * Note for anyone adding `actions`: a popover or menu opening from
         * here would be cropped by this. Portal it, as the @profylr/ui overlay
         * primitives already do.
         */
        "overflow-hidden",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs14("div", { className: "flex min-w-0 items-center gap-3", children: [
          onMenuToggle && /* @__PURE__ */ jsx17(
            "button",
            {
              type: "button",
              "aria-label": "Open navigation menu",
              onClick: onMenuToggle,
              className: "focus-ring rounded-md p-1.5 text-text-muted outline-none hover:text-text md:hidden",
              children: /* @__PURE__ */ jsx17(MenuIcon, {})
            }
          ),
          identity && /*
           * Constrains height so any logo fits the bar, and nothing else. No
           * background, no border, no padding of its own -- whatever the app
           * supplies is what appears.
           */
          /* @__PURE__ */ jsx17("div", { className: "flex min-w-0 items-center text-h4 font-medium text-text [&_img]:max-h-11 [&_img]:w-auto [&_img]:object-contain [&_svg]:max-h-11", children: identity })
        ] }),
        /* @__PURE__ */ jsxs14("div", { className: "flex min-w-0 items-center gap-4", children: [
          /* @__PURE__ */ jsxs14("div", { className: "relative flex min-w-0 items-center", children: [
            productMark && /*
             * Larger than the bar and anchored past its corner, so the header's
             * own edges crop it. The bleed is the point -- a mark that fits
             * neatly reads as a second logo competing with the tenant's.
             */
            /* @__PURE__ */ jsx17(
              "span",
              {
                "aria-hidden": "true",
                className: cn(
                  /*
                   * Overhangs the right edge just enough to read as a bleed,
                   * without cropping so much of the mark's curve that it looks
                   * like a rendering fault. Halfway between flush and hanging
                   * off the corner.
                   */
                  "pointer-events-none absolute -top-24 -right-10 flex items-center",
                  "[&_img]:h-64 [&_img]:w-auto [&_img]:object-contain [&_svg]:h-64",
                  /*
                   * Carries both halves of the mark's theme treatment: the
                   * inversion, and the opacity that used to be an `opacity-10`
                   * utility here.
                   *
                   * Apps supply one fixed-fill asset -- a dark mark drawn for the
                   * white bar -- so on the dark bar it has to be inverted, and
                   * once inverted it reads quieter than the original does on
                   * white, so dark carries the higher figure. Both live in the
                   * token layer rather than in `dark:` variants because the
                   * estate switches theme by class, not by prefers-color-scheme.
                   */
                  "header-mark"
                ),
                children: productMark
              }
            ),
            /* @__PURE__ */ jsx17("p", { className: "relative truncate text-h2 font-medium text-text", children: product })
          ] }),
          actions
        ] })
      ]
    }
  );
}
function MenuIcon() {
  return /* @__PURE__ */ jsx17(
    "svg",
    {
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      className: "size-6",
      "aria-hidden": "true",
      children: /* @__PURE__ */ jsx17("path", { d: "M4 6h16M4 12h16M4 18h16" })
    }
  );
}

// src/components/auth-shell.tsx
import * as React17 from "react";
import { Fragment as Fragment6, jsx as jsx18, jsxs as jsxs15 } from "react/jsx-runtime";
function AuthShell({
  product,
  brandMark,
  identity,
  title = "Welcome",
  description,
  children,
  footnote,
  poweredBy,
  className
}) {
  const titleId = React17.useId();
  return /* @__PURE__ */ jsxs15(
    "div",
    {
      className: cn(
        "gradient-auth relative flex min-h-screen flex-col overflow-hidden",
        /*
         * The two panels sit together in the middle rather than at opposite
         * edges of the viewport. A half-and-half split pushes the mark and the
         * card as far apart as the screen allows, which on a wide monitor
         * reads as two unrelated things rather than one sign-in screen.
         */
        "items-center justify-center gap-4 lg:flex-row lg:gap-16",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs15("div", { className: "relative flex flex-col items-center justify-center gap-8 px-8 pt-16 lg:pt-0", children: [
          brandMark && /* @__PURE__ */ jsxs15("div", { className: "relative flex items-center justify-center", children: [
            /* @__PURE__ */ jsx18("div", { "aria-hidden": "true", className: "animate-bob-shadow mark-shadow" }),
            /* @__PURE__ */ jsx18("div", { className: "animate-bob relative flex items-center justify-center", children: brandMark })
          ] }),
          /* @__PURE__ */ jsx18("h1", { className: "m-0 text-display text-center font-medium text-text-nav", children: product })
        ] }),
        /* @__PURE__ */ jsx18("div", { className: "relative flex items-center justify-center px-6 pb-16 lg:px-0 lg:pb-0", children: /* @__PURE__ */ jsxs15(
          "section",
          {
            "aria-labelledby": titleId,
            className: cn("w-full max-w-sm surface-glass p-8", "flex flex-col gap-6"),
            children: [
              identity && /* @__PURE__ */ jsxs15(Fragment6, { children: [
                /* @__PURE__ */ jsx18("div", { className: "flex min-w-0 items-center text-h4 font-medium text-text-nav [&_img]:max-h-9 [&_img]:w-auto [&_img]:object-contain [&_svg]:max-h-9", children: identity }),
                /* @__PURE__ */ jsx18("div", { className: "h-px divider-glass" })
              ] }),
              /* @__PURE__ */ jsxs15("div", { className: "flex flex-col gap-2", children: [
                /* @__PURE__ */ jsx18("h2", { id: titleId, className: "m-0 text-h2 font-medium text-text-nav", children: title }),
                description && /* @__PURE__ */ jsx18("p", { className: "text-body text-text-nav-secondary", children: description })
              ] }),
              children,
              footnote && /* @__PURE__ */ jsx18("p", { className: "text-center text-caption text-text-nav-secondary", children: footnote })
            ]
          }
        ) }),
        poweredBy && /* @__PURE__ */ jsx18("div", { className: "relative flex items-center justify-center gap-2 pb-8 text-caption text-text-nav-muted lg:absolute lg:inset-x-0 lg:bottom-6 lg:pb-0", children: poweredBy })
      ]
    }
  );
}

// src/components/theme-toggle.tsx
import * as React18 from "react";
import { jsx as jsx19, jsxs as jsxs16 } from "react/jsx-runtime";
var STORAGE_KEY = "profylr-theme";
function apply(theme) {
  const root = document.documentElement;
  root.setAttribute("data-theme", theme);
  root.classList.toggle("dark", theme === "dark");
}
function useTheme({ storageKey = STORAGE_KEY, followSystem = false } = {}) {
  const [theme, setThemeState] = React18.useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === "light" || stored === "dark") return stored;
    } catch {
    }
    if (followSystem && typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
    return "light";
  });
  React18.useEffect(() => {
    apply(theme);
    try {
      localStorage.setItem(storageKey, theme);
    } catch {
    }
  }, [theme, storageKey]);
  const setTheme = React18.useCallback((next) => setThemeState(next), []);
  const toggle = React18.useCallback(
    () => setThemeState((current) => current === "dark" ? "light" : "dark"),
    []
  );
  return { theme, setTheme, toggle };
}
function ThemeToggle({ className, ...options }) {
  const { theme, toggle } = useTheme(options);
  const isDark = theme === "dark";
  return /* @__PURE__ */ jsx19(
    "button",
    {
      type: "button",
      onClick: toggle,
      role: "switch",
      "aria-checked": isDark,
      "aria-label": "Dark mode",
      title: isDark ? "Switch to light mode" : "Switch to dark mode",
      className: cn(
        "focus-ring rounded-md p-2.5 outline-none transition-colors duration-fast ease-move",
        "text-text-nav-muted hover:text-text-nav",
        className
      ),
      children: isDark ? /* @__PURE__ */ jsx19(SunIcon, {}) : /* @__PURE__ */ jsx19(MoonIcon, {})
    }
  );
}
function MoonIcon() {
  return /* @__PURE__ */ jsx19(
    "svg",
    {
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className: "size-6",
      "aria-hidden": "true",
      children: /* @__PURE__ */ jsx19("path", { d: "M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" })
    }
  );
}
function SunIcon() {
  return /* @__PURE__ */ jsxs16(
    "svg",
    {
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className: "size-6",
      "aria-hidden": "true",
      children: [
        /* @__PURE__ */ jsx19("circle", { cx: "12", cy: "12", r: "4" }),
        /* @__PURE__ */ jsx19("path", { d: "M12 2v2m0 16v2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M2 12h2m16 0h2M4.9 19.1l1.4-1.4m11.4-11.4 1.4-1.4" })
      ]
    }
  );
}

// src/components/nav-shell.tsx
import * as React19 from "react";
import { Fragment as Fragment8, jsx as jsx20, jsxs as jsxs17 } from "react/jsx-runtime";
var RECENT_CAP = 4;
var PANEL_EXIT_MS = 200;
var HOVER = "nav-hover";
function ActiveRail() {
  return /* @__PURE__ */ jsx20(
    "span",
    {
      "aria-hidden": "true",
      className: "absolute inset-y-0 left-0 w-0.5 rounded-pill bg-accent-bright"
    }
  );
}
function useRovingFocus(itemCount, activeIndex) {
  const [focusIndex, setFocusIndex] = React19.useState(activeIndex);
  const refs = React19.useRef([]);
  React19.useEffect(() => {
    setFocusIndex(activeIndex);
  }, [activeIndex]);
  const focusItem = React19.useCallback((index) => {
    const clamped = Math.max(0, Math.min(index, refs.current.length - 1));
    setFocusIndex(clamped);
    refs.current[clamped]?.focus();
  }, []);
  const onKeyDown = React19.useCallback(
    (index, event, onCross) => {
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          focusItem((index + 1) % itemCount);
          break;
        case "ArrowUp":
          event.preventDefault();
          focusItem((index - 1 + itemCount) % itemCount);
          break;
        case "Home":
          event.preventDefault();
          focusItem(0);
          break;
        case "End":
          event.preventDefault();
          focusItem(itemCount - 1);
          break;
        default:
          if (onCross) onCross();
      }
    },
    [focusItem, itemCount]
  );
  return { focusIndex, refs, onKeyDown, focusItem };
}
function NavShell({
  hubs,
  activeHubId,
  activePageId,
  recent = [],
  activeRecentId,
  collapsed = false,
  onCollapsedChange,
  brand,
  footer,
  onHubSelect,
  renderLink,
  className
}) {
  const activeHub = hubs.find((hub) => hub.id === activeHubId) ?? hubs[0];
  const [peeking, setPeeking] = React19.useState(false);
  React19.useEffect(() => {
    if (!collapsed) setPeeking(false);
  }, [collapsed]);
  const panelVisible = !collapsed || peeking;
  const selectHub = (hubId) => {
    if (panelVisible && hubId === activeHub?.id) {
      setPeeking(false);
      onCollapsedChange?.(true);
      return;
    }
    onHubSelect?.(hubId);
    if (collapsed) setPeeking(true);
  };
  const panelRef = React19.useRef(null);
  const [mounted, setMounted] = React19.useState(panelVisible);
  const [open, setOpen] = React19.useState(panelVisible);
  React19.useEffect(() => {
    if (panelVisible) {
      setMounted(true);
      let second = 0;
      const first = requestAnimationFrame(() => {
        second = requestAnimationFrame(() => setOpen(true));
      });
      return () => {
        cancelAnimationFrame(first);
        cancelAnimationFrame(second);
      };
    }
    setOpen(false);
    const reduced = typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timer = setTimeout(() => setMounted(false), reduced ? 0 : PANEL_EXIT_MS);
    return () => clearTimeout(timer);
  }, [panelVisible]);
  React19.useEffect(() => {
    if (!panelVisible) return;
    const closePanel = () => {
      setPeeking(false);
      onCollapsedChange?.(true);
    };
    const onKey = (event) => {
      if (event.key === "Escape") closePanel();
    };
    const onPointerDown = (event) => {
      const target = event.target;
      if (panelRef.current && !panelRef.current.contains(target)) {
        if (!(target instanceof Element && target.closest("[data-nav-rail]"))) {
          closePanel();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [panelVisible]);
  const cappedRecent = recent.slice(0, RECENT_CAP);
  const activeHubIndex = Math.max(
    0,
    hubs.findIndex((hub) => hub.id === activeHubId)
  );
  const rail = useRovingFocus(hubs.length, activeHubIndex);
  const panelEntries = React19.useMemo(
    () => [
      ...(activeHub?.pages ?? []).map((page) => ({ kind: "page", page })),
      ...cappedRecent.map((entry) => ({ kind: "recent", entry }))
    ],
    [activeHub, cappedRecent]
  );
  const activePanelIndex = Math.max(
    0,
    panelEntries.findIndex(
      (entry) => entry.kind === "page" ? entry.page.id === activePageId : entry.entry.id === activeRecentId
    )
  );
  const panel = useRovingFocus(panelEntries.length, activePanelIndex);
  const link = renderLink ?? (({ href, ref, ...props }) => /* @__PURE__ */ jsx20("a", { href, ref, ...props }));
  const rowBase = "focus-ring relative flex items-center gap-2 rounded-md outline-none transition-colors duration-fast ease-move";
  return /* @__PURE__ */ jsxs17(
    "div",
    {
      className: cn(
        /*
         * Only the rail occupies layout space -- the panel always floats above
         * the page rather than reflowing it, at every breakpoint.
         *
         * There is deliberately no docked mode. Docking makes opening the panel
         * reflow the page and every table on it, and a reload would then shove
         * the content sideways before the user has done anything. A sidebar
         * that moves the page out from under you is worse than one that
         * briefly covers it.
         */
        "relative flex h-full w-26 shrink-0 bg-surface-nav",
        className
      ),
      "data-collapsed": collapsed || void 0,
      children: [
        /* @__PURE__ */ jsxs17(
          "div",
          {
            "data-nav-rail": true,
            className: "relative z-50 flex w-26 shrink-0 flex-col items-center border-r border-border-nav-subtle bg-surface-nav-deep py-4 shadow-nav",
            children: [
              /* @__PURE__ */ jsx20("div", { className: "mb-4 flex size-16 items-center justify-center", title: brand.name, children: brand.logo }),
              /* @__PURE__ */ jsx20("nav", { "aria-label": "Sections", className: "flex flex-col items-center gap-0.5", children: hubs.map((hub, index) => {
                const active = hub.id === activeHub?.id;
                const classes = cn(
                  /*
                   * Wider than the spec's 3.75rem. "Performance" is the longest
                   * single-word hub and must not truncate or break mid-word, which
                   * sets the floor: at 0.6875rem Inter 500 it needs about 4.25rem of
                   * label, so the item is 5.25rem inside a 5.5rem rail.
                   */
                  "focus-ring relative flex w-25 flex-col items-center rounded-md px-1 py-2.5 outline-none",
                  "transition-colors duration-fast ease-move",
                  // Same treatment as a page row: accent bar, wash fading to
                  // transparent, full-weight label. One active state to learn, and
                  // the rail and panel read as one control rather than two.
                  active ? "nav-wash font-medium text-text-nav" : cn("text-text-nav-muted", HOVER, "hover:text-text-nav")
                );
                const inner = /* @__PURE__ */ jsxs17(Fragment8, { children: [
                  active && /* @__PURE__ */ jsx20(ActiveRail, {}),
                  /* @__PURE__ */ jsx20("span", { "aria-hidden": "true", className: "[&>svg]:size-6", children: hub.icon }),
                  /* @__PURE__ */ jsx20("span", { className: "mt-1 w-full text-balance hyphens-none text-center text-nav-label", children: hub.label })
                ] });
                const onKeyDown = (event) => rail.onKeyDown(index, event, () => {
                  if (event.key === "ArrowRight") {
                    event.preventDefault();
                    panel.focusItem(0);
                  }
                });
                if (hub.href) {
                  return /* @__PURE__ */ jsx20(React19.Fragment, { children: link({
                    href: hub.href,
                    className: classes,
                    children: inner,
                    onClick: () => {
                      setPeeking(false);
                      onCollapsedChange?.(true);
                    },
                    "aria-current": active ? "true" : void 0,
                    tabIndex: rail.focusIndex === index ? 0 : -1,
                    onKeyDown,
                    ref: (node) => {
                      rail.refs.current[index] = node;
                    }
                  }) }, hub.id);
                }
                return /* @__PURE__ */ jsx20(
                  "button",
                  {
                    type: "button",
                    ref: (node) => {
                      rail.refs.current[index] = node;
                    },
                    tabIndex: rail.focusIndex === index ? 0 : -1,
                    "aria-current": active ? "true" : void 0,
                    onClick: () => selectHub(hub.id),
                    onKeyDown,
                    className: classes,
                    children: inner
                  },
                  hub.id
                );
              }) }),
              footer && /*
               * mt-auto pins this to the bottom however few hubs there are -- a
               * restricted user may see two, and the controls should not float
               * halfway up an empty rail.
               *
               * It centres what it is given and restyles nothing else. It used to
               * force padding onto every descendant button so app controls matched
               * the rail's icons; that padding also landed on the user-menu
               * trigger, overriding its own `p-0`. With border-box sizing the
               * trigger's content box shrank to 0.625rem and the 2.5rem avatar
               * inside overflowed from that edge, sitting 0.59rem right of centre.
               * A wrapper that quietly restyles arbitrary app content will always
               * find something it does not fit.
               */
              /* @__PURE__ */ jsxs17("div", { className: "mt-auto flex w-full flex-col items-center gap-3 pt-4", children: [
                /* @__PURE__ */ jsx20("div", { className: "h-px w-12 bg-border-nav-subtle" }),
                footer
              ] })
            ]
          }
        ),
        mounted && /* @__PURE__ */ jsxs17(
          "nav",
          {
            ref: panelRef,
            "aria-label": "Pages",
            "aria-hidden": !panelVisible || void 0,
            className: cn(
              "hidden min-w-0 flex-col md:flex",
              // Always floating. See the note on the shell above.
              "absolute left-26 top-0 z-40 h-full w-68 border-r border-border-nav-subtle bg-surface-nav shadow-dialog",
              /*
               * `transition`, not `transition-[transform,opacity]`. Tailwind v4
               * compiles translate-x-* to the `translate` property rather than
               * to `transform`, so naming transform explicitly animated the fade
               * and let the slide jump.
               */
              "transition duration-base",
              open ? "translate-x-0 opacity-100 ease-enter" : "-translate-x-3 opacity-0 ease-exit",
              !panelVisible && "pointer-events-none"
            ),
            children: [
              /* @__PURE__ */ jsx20(
                "span",
                {
                  "aria-hidden": "true",
                  className: "gradient-nav-ambient pointer-events-none absolute inset-x-0 bottom-0 h-40"
                }
              ),
              /* @__PURE__ */ jsxs17("div", { className: "relative flex items-center justify-between px-2.5 pb-2 pt-4", children: [
                /* @__PURE__ */ jsx20("p", { className: "text-body font-medium text-text-nav-secondary", children: activeHub?.label }),
                onCollapsedChange && /*
                 * This chevron always means "less navigation": it closes a
                 * floating panel, and collapses a docked one.
                 *
                 * It used to dock the panel when floating, which meant the first
                 * click on what looks like a collapse control made the sidebar
                 * wider and shoved the page across -- and only the second click
                 * collapsed anything. Docking is still available, from the
                 * expand control on the collapsed rail.
                 */
                /* @__PURE__ */ jsx20(
                  "button",
                  {
                    type: "button",
                    "aria-label": "Close navigation panel",
                    "aria-expanded": true,
                    onClick: () => {
                      setPeeking(false);
                      onCollapsedChange(true);
                    },
                    className: "focus-ring rounded p-0.5 text-text-nav-muted outline-none hover:text-text-nav",
                    children: /* @__PURE__ */ jsx20(ChevronLeftIcon, {})
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs17("div", { className: "relative min-h-0 flex-1 overflow-y-auto px-1.5 pb-4", children: [
                activeHub && activeHub.pages.length === 0 ? (
                  /* Section 5.3 — a hub that is itself the destination. */
                  /* @__PURE__ */ jsxs17("p", { className: "px-3 py-2 text-body-small text-text-nav-muted", children: [
                    activeHub.label,
                    " has no sub-pages."
                  ] })
                ) : /* @__PURE__ */ jsx20("ul", { "aria-label": `${activeHub?.label} pages`, className: "space-y-0.5", children: activeHub?.pages.map((page, index) => {
                  const active = page.id === activePageId;
                  return /* @__PURE__ */ jsx20("li", { children: link({
                    href: page.href,
                    className: cn(
                      rowBase,
                      "min-h-10 px-3 py-2.5 text-body",
                      active ? "nav-wash font-medium text-text-nav" : cn("text-text-nav-secondary", HOVER, "hover:text-text-nav")
                    ),
                    "aria-current": active ? "page" : void 0,
                    title: page.label,
                    onClick: () => {
                      setPeeking(false);
                      onCollapsedChange?.(true);
                    },
                    tabIndex: panel.focusIndex === index ? 0 : -1,
                    onKeyDown: (event) => panel.onKeyDown(index, event, () => {
                      if (event.key === "ArrowLeft") {
                        event.preventDefault();
                        rail.focusItem(activeHubIndex);
                      }
                    }),
                    ref: (node) => {
                      panel.refs.current[index] = node;
                    },
                    children: /* @__PURE__ */ jsxs17(Fragment8, { children: [
                      active && /* @__PURE__ */ jsx20(ActiveRail, {}),
                      /* @__PURE__ */ jsx20("span", { "aria-hidden": "true", className: "shrink-0 [&>svg]:size-5", children: page.icon }),
                      /* @__PURE__ */ jsx20("span", { className: "truncate", children: page.label })
                    ] })
                  }) }, page.id);
                }) }),
                cappedRecent.length > 0 && /* @__PURE__ */ jsxs17(Fragment8, { children: [
                  /* @__PURE__ */ jsx20("div", { className: "mx-1 my-3 h-px bg-border-nav-subtle" }),
                  /* @__PURE__ */ jsx20(
                    "p",
                    {
                      id: "nav-recent-heading",
                      className: "px-1 pb-1 text-body font-medium text-text-nav-secondary",
                      children: "Recent"
                    }
                  ),
                  /* @__PURE__ */ jsx20("ul", { "aria-labelledby": "nav-recent-heading", className: "space-y-0.5", children: cappedRecent.map((entry, i) => {
                    const index = (activeHub?.pages.length ?? 0) + i;
                    const active = entry.id === activeRecentId;
                    return /* @__PURE__ */ jsx20("li", { children: link({
                      href: entry.href,
                      // Truncated to one line, so the full name lives here.
                      title: entry.label,
                      className: cn(
                        rowBase,
                        "flex-col !items-start px-3 py-1.5",
                        active ? "nav-wash" : HOVER
                      ),
                      "aria-current": active ? "page" : void 0,
                      onClick: () => {
                        setPeeking(false);
                        onCollapsedChange?.(true);
                      },
                      tabIndex: panel.focusIndex === index ? 0 : -1,
                      onKeyDown: (event) => panel.onKeyDown(index, event, () => {
                        if (event.key === "ArrowLeft") {
                          event.preventDefault();
                          rail.focusItem(activeHubIndex);
                        }
                      }),
                      ref: (node) => {
                        panel.refs.current[index] = node;
                      },
                      children: /* @__PURE__ */ jsxs17(Fragment8, { children: [
                        active && /* @__PURE__ */ jsx20(ActiveRail, {}),
                        /* @__PURE__ */ jsx20(
                          "span",
                          {
                            className: cn(
                              "w-full truncate text-body-small",
                              active ? "text-text-nav" : "text-text-nav-secondary"
                            ),
                            children: entry.label
                          }
                        ),
                        /* @__PURE__ */ jsx20("span", { className: "w-full truncate text-nav-meta text-text-nav-muted", children: entry.parentLabel })
                      ] })
                    }) }, entry.id);
                  }) })
                ] })
              ] })
            ]
          }
        )
      ]
    }
  );
}
function ChevronLeftIcon() {
  return /* @__PURE__ */ jsx20("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", className: "size-4", "aria-hidden": "true", children: /* @__PURE__ */ jsx20("path", { d: "m15 18-6-6 6-6" }) });
}

// src/components/nav-shell-helpers.ts
import * as React20 from "react";
function matchesPath(href, pathname) {
  if (href === pathname) return true;
  if (href === "/") return false;
  return pathname.startsWith(href.endsWith("/") ? href : `${href}/`);
}
function resolveActiveNav(hubs, pathname, { matches = (page, path) => matchesPath(page.href, path) } = {}) {
  let best;
  for (const hub of hubs) {
    for (const page of hub.pages) {
      if (!matches(page, pathname)) continue;
      const score = page.href.length;
      if (!best || score > best.score) best = { hub, page, score };
    }
  }
  if (best) return { activeHubId: best.hub.id, activePageId: best.page.id };
  const destination = hubs.find((hub) => hub.href && matches({ ...EMPTY_PAGE, href: hub.href }, pathname));
  if (destination) return { activeHubId: destination.id };
  return { activeHubId: hubs[0]?.id };
}
var EMPTY_PAGE = { id: "", label: "", icon: null, href: "" };
function buildHubs(sources, { isVisible = () => true, toPage, collapseSinglePage = true }) {
  return sources.map((source) => {
    const pages = source.items.filter(isVisible).map(toPage);
    return { id: source.id, label: source.label, icon: source.icon, pages };
  }).filter((hub) => hub.pages.length > 0).map(
    (hub) => collapseSinglePage && hub.pages.length === 1 ? { ...hub, href: hub.pages[0].href, pages: [] } : hub
  );
}
var DEFAULT_CAP = 4;
function useRecentEntries({
  scope = "default",
  storageKey = "profylr-recent",
  limit = DEFAULT_CAP
} = {}) {
  const key = `${storageKey}:${scope}`;
  const read = React20.useCallback(() => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(
        (entry) => typeof entry === "object" && entry !== null && typeof entry.id === "string" && typeof entry.label === "string" && typeof entry.parentLabel === "string" && typeof entry.href === "string"
      );
    } catch {
      return [];
    }
  }, [key]);
  const [entries, setEntries] = React20.useState(read);
  React20.useEffect(() => {
    setEntries(read());
  }, [read]);
  const write = React20.useCallback(
    (next) => {
      setEntries(next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
      }
    },
    [key]
  );
  const record = React20.useCallback(
    (entry) => {
      setEntries((previous) => {
        const next = [entry, ...previous.filter((e) => e.id !== entry.id)].slice(0, limit);
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch {
        }
        return next;
      });
    },
    [key, limit]
  );
  const clear = React20.useCallback(() => write([]), [write]);
  return { entries, record, clear };
}
function useRecordRecent(api, entry) {
  const { record } = api;
  const id = entry?.id;
  const label = entry?.label;
  const parentLabel = entry?.parentLabel;
  const href = entry?.href;
  React20.useEffect(() => {
    if (!id || !label || !parentLabel || !href) return;
    record({ id, label, parentLabel, href });
  }, [id, label, parentLabel, href, record]);
}

// src/components/permission-gate.tsx
import { Fragment as Fragment9, jsx as jsx21, jsxs as jsxs18 } from "react/jsx-runtime";
function PermissionGate({
  allowed,
  mode,
  reason,
  explainTitle = "You don't have access to this",
  explainAction,
  className,
  children
}) {
  if (allowed) return /* @__PURE__ */ jsx21(Fragment9, { children });
  if (mode === "hide") return null;
  if (mode === "disable") {
    return /* @__PURE__ */ jsx21(TooltipProvider, { children: /* @__PURE__ */ jsxs18(Tooltip, { children: [
      /* @__PURE__ */ jsx21(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx21(
        "span",
        {
          tabIndex: 0,
          "aria-disabled": "true",
          "aria-describedby": void 0,
          className: cn("inline-flex cursor-not-allowed opacity-50 outline-none", className),
          children: /* @__PURE__ */ jsx21(
            "span",
            {
              inert: "",
              className: "pointer-events-none contents",
              children
            }
          )
        }
      ) }),
      reason ? /* @__PURE__ */ jsx21(TooltipContent, { children: reason }) : null
    ] }) });
  }
  return /* @__PURE__ */ jsxs18("div", { role: "note", className: cn("flex flex-col items-start gap-2", className), children: [
    /* @__PURE__ */ jsx21("p", { className: "text-body font-medium text-text", children: explainTitle }),
    reason ? /* @__PURE__ */ jsx21("p", { className: "text-body-sm text-text-muted", children: reason }) : null,
    explainAction ? /* @__PURE__ */ jsx21("div", { className: "mt-1", children: explainAction }) : null
  ] });
}
PermissionGate.displayName = "PermissionGate";

// src/components/skeleton.tsx
import * as React21 from "react";
import { jsx as jsx22 } from "react/jsx-runtime";
var SHAPES = {
  text: "h-4 w-full rounded-sm",
  heading: "h-5 w-1/2 rounded-sm",
  block: "h-24 w-full rounded-sm",
  figure: "h-8 w-24 rounded-sm",
  circle: "size-8 rounded-pill"
};
var Skeleton = React21.forwardRef(
  ({ shape = "text", className, ...props }, ref) => /* @__PURE__ */ jsx22(
    "span",
    {
      ref,
      "aria-hidden": "true",
      className: cn("block animate-pulse bg-surface-sunken", SHAPES[shape], className),
      ...props
    }
  )
);
Skeleton.displayName = "Skeleton";
var SkeletonLines = React21.forwardRef(
  ({ lines = 3, className, ...props }, ref) => /* @__PURE__ */ jsx22("div", { ref, "aria-hidden": "true", className: cn("flex flex-col gap-2", className), ...props, children: Array.from({ length: lines }).map((_, i) => /* @__PURE__ */ jsx22(Skeleton, { shape: "text", className: i === lines - 1 ? "w-2/3" : void 0 }, i)) })
);
SkeletonLines.displayName = "SkeletonLines";

// src/components/status.tsx
import * as React22 from "react";
import { jsx as jsx23, jsxs as jsxs19 } from "react/jsx-runtime";
var STATUS_GLYPHS = {
  compliant: "\u25CF",
  warning: "\u25B2",
  degraded: "\u25C6",
  breach: "\u25A0",
  "not-assessed": "\u2014"
};
var TONE_CLASS = {
  compliant: "text-status-compliant",
  warning: "text-status-warning",
  degraded: "text-status-degraded",
  breach: "text-status-breach",
  "not-assessed": "text-status-not-assessed"
};
var Status = React22.forwardRef(
  ({ tone, size = "sm", className, children, ...props }, ref) => /* @__PURE__ */ jsxs19(
    "span",
    {
      ref,
      "data-tone": tone,
      className: cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap font-medium",
        size === "sm" ? "text-body-sm" : "text-body",
        TONE_CLASS[tone],
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx23("span", { "aria-hidden": "true", children: STATUS_GLYPHS[tone] }),
        /* @__PURE__ */ jsx23("span", { children })
      ]
    }
  )
);
Status.displayName = "Status";

// src/components/metric.tsx
import * as React23 from "react";
import { Fragment as Fragment10, jsx as jsx24, jsxs as jsxs20 } from "react/jsx-runtime";
var FIGURE = {
  lg: "metric-lg",
  md: "metric-md",
  sm: "metric-sm"
};
var UNIT = {
  lg: "metric-md text-text-muted",
  md: "metric-sm text-text-muted",
  sm: "text-body-sm text-text-muted"
};
function isEmpty(value) {
  return value === null || value === void 0 || value === "";
}
var Metric = React23.forwardRef(
  ({ label, value, unit, size = "md", status, className, ...props }, ref) => /* @__PURE__ */ jsxs20("div", { ref, className: cn("flex min-w-0 flex-col gap-2", className), ...props, children: [
    /* @__PURE__ */ jsx24("p", { className: "eyebrow", children: label }),
    /* @__PURE__ */ jsx24("p", { className: cn(FIGURE[size], "text-text"), children: isEmpty(value) ? /* @__PURE__ */ jsx24("span", { className: "text-text-muted", "aria-label": "No value", children: "\u2014" }) : /* @__PURE__ */ jsxs20(Fragment10, { children: [
      value,
      unit ? /* @__PURE__ */ jsx24("span", { className: cn("ml-0.5", UNIT[size]), children: unit }) : null
    ] }) }),
    status ? /* @__PURE__ */ jsx24(Status, { tone: status.tone, children: status.label }) : null
  ] })
);
Metric.displayName = "Metric";
var MetricRow = React23.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx24("div", { ref, className: cn("flex flex-wrap gap-8", className), ...props })
);
MetricRow.displayName = "MetricRow";

// src/components/section.tsx
import * as React24 from "react";
import { jsx as jsx25, jsxs as jsxs21 } from "react/jsx-runtime";
var Section = React24.forwardRef(
  ({ title, description, actions, level = 2, className, children, ...props }, ref) => {
    const generated = React24.useId();
    const eyebrowId = `section-${generated}-eyebrow`;
    const Heading = `h${level}`;
    return /* @__PURE__ */ jsxs21(
      "section",
      {
        ref,
        "aria-labelledby": eyebrowId,
        className: cn("flex min-w-0 flex-col gap-2", className),
        ...props,
        children: [
          /* @__PURE__ */ jsxs21("div", { className: "flex items-baseline justify-between gap-3", children: [
            /* @__PURE__ */ jsxs21("div", { className: "flex min-w-0 flex-col gap-1", children: [
              /* @__PURE__ */ jsx25(Heading, { id: eyebrowId, className: "m-0 eyebrow", children: title }),
              description ? /* @__PURE__ */ jsx25("p", { className: "text-body-sm text-text-muted", children: description }) : null
            ] }),
            actions ? /* @__PURE__ */ jsx25("div", { className: "flex shrink-0 items-center gap-2", children: actions }) : null
          ] }),
          children
        ]
      }
    );
  }
);
Section.displayName = "Section";

// src/components/detail-field.tsx
import * as React25 from "react";
import { jsx as jsx26, jsxs as jsxs22 } from "react/jsx-runtime";
var DEFAULT_LABEL_WIDTH = "8rem";
var DetailListContext = React25.createContext({
  layout: "stacked",
  labelWidth: DEFAULT_LABEL_WIDTH
});
var DetailList = React25.forwardRef(
  ({ layout = "stacked", labelWidth = DEFAULT_LABEL_WIDTH, className, children, ...props }, ref) => {
    const context = React25.useMemo(() => ({ layout, labelWidth }), [layout, labelWidth]);
    return /* @__PURE__ */ jsx26(DetailListContext.Provider, { value: context, children: /* @__PURE__ */ jsx26(
      "dl",
      {
        ref,
        "data-layout": layout,
        className: cn(
          "flex min-w-0 flex-col",
          // Rows in a compact panel sit close; stacked pairs are siblings
          // with their own internal gap, so they take the wider step.
          layout === "inline" ? "gap-2" : "gap-6",
          className
        ),
        ...props,
        children
      }
    ) });
  }
);
DetailList.displayName = "DetailList";
function isEmptyValue(value) {
  if (value === null || value === void 0 || typeof value === "boolean") return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.every(isEmptyValue);
  return false;
}
var DetailField = React25.forwardRef(
  ({ label, mono = false, className, children, ...props }, ref) => {
    const { layout, labelWidth } = React25.useContext(DetailListContext);
    const inline = layout === "inline";
    const empty = isEmptyValue(children);
    return (
      // A div grouping one dt with its dd is valid inside a dl, and it is
      // what lets a pair lay out as a row without leaving the list.
      /* @__PURE__ */ jsxs22(
        "div",
        {
          ref,
          className: cn(
            "flex min-w-0",
            inline ? "flex-row items-baseline gap-3" : "flex-col gap-2",
            className
          ),
          ...props,
          children: [
            /* @__PURE__ */ jsx26("dt", { className: "eyebrow shrink-0", style: inline ? { minWidth: labelWidth } : void 0, children: label }),
            /* @__PURE__ */ jsx26(
              "dd",
              {
                className: cn("min-w-0 text-body text-text", mono && "font-mono"),
                "data-numeric": mono ? "" : void 0,
                children: empty ? /* @__PURE__ */ jsx26("span", { className: "text-text-muted", "aria-label": "No value", children: "\u2014" }) : children
              }
            )
          ]
        }
      )
    );
  }
);
DetailField.displayName = "DetailField";

// src/components/prose.tsx
import * as React26 from "react";
import { jsx as jsx27, jsxs as jsxs23 } from "react/jsx-runtime";
var MEASURE = "36rem";
var Prose = React26.forwardRef(
  ({ attribution, className, style, children, ...props }, ref) => /* @__PURE__ */ jsxs23(
    "div",
    {
      ref,
      className: cn("flex min-w-0 flex-col gap-3 text-body text-text", className),
      style: { ...style, maxWidth: MEASURE },
      ...props,
      children: [
        typeof children === "string" ? /* @__PURE__ */ jsx27("p", { children }) : children,
        attribution ? /* @__PURE__ */ jsx27("p", { className: "text-caption text-text-muted", children: attribution }) : null
      ]
    }
  )
);
Prose.displayName = "Prose";

// src/components/callout.tsx
import * as React27 from "react";
import { jsx as jsx28, jsxs as jsxs24 } from "react/jsx-runtime";
var RULE = {
  compliant: "border-l-status-compliant",
  warning: "border-l-status-warning",
  degraded: "border-l-status-degraded",
  breach: "border-l-status-breach",
  "not-assessed": "border-l-status-not-assessed",
  neutral: "border-l-divider"
};
var STATUS = {
  compliant: { glyph: "\u25CF", word: "Compliant", tone: "text-status-compliant" },
  warning: { glyph: "\u25B2", word: "At risk", tone: "text-status-warning" },
  degraded: { glyph: "\u25C6", word: "Degraded", tone: "text-status-degraded" },
  breach: { glyph: "\u25A0", word: "Breach", tone: "text-status-breach" },
  "not-assessed": { glyph: "\u2014", word: "Not assessed", tone: "text-status-not-assessed" }
};
var Callout = React27.forwardRef(
  ({ tone = "neutral", title, live = false, statusLabel, className, children, ...props }, ref) => {
    const status = tone === "neutral" ? null : STATUS[tone];
    return /* @__PURE__ */ jsxs24(
      "div",
      {
        ref,
        role: live ? "status" : "note",
        "data-tone": tone,
        className: cn("flex min-w-0 flex-col gap-1 border-l-2 pl-3", RULE[tone], className),
        ...props,
        children: [
          /* @__PURE__ */ jsxs24("p", { className: "text-body font-medium text-text", children: [
            status ? /* @__PURE__ */ jsxs24("span", { className: cn("mr-2 whitespace-nowrap", status.tone), children: [
              /* @__PURE__ */ jsx28("span", { "aria-hidden": "true", children: status.glyph }),
              " ",
              statusLabel ?? status.word,
              /* @__PURE__ */ jsx28("span", { className: "sr-only", children: ":" })
            ] }) : null,
            title
          ] }),
          children ? /* @__PURE__ */ jsx28("div", { className: "text-body-sm text-text-muted", children }) : null
        ]
      }
    );
  }
);
Callout.displayName = "Callout";

// src/components/machine-generated.tsx
import * as React29 from "react";

// src/components/ai-marker.tsx
import { Sparkles } from "lucide-react";
import * as React28 from "react";
import { jsx as jsx29, jsxs as jsxs25 } from "react/jsx-runtime";
var AI_MARKER_WORD = {
  content: "AI generated",
  action: "AI-powered"
};
var AiMarker = React28.forwardRef(
  ({ marks = "content", labelling = "assistive", label, tone = "accent", className, ...props }, ref) => /* @__PURE__ */ jsxs25(
    "span",
    {
      ref,
      "data-ai-marker": marks,
      className: cn("inline-flex items-center gap-1 whitespace-nowrap align-middle", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx29(
          Sparkles,
          {
            width: "1em",
            height: "1em",
            "aria-hidden": "true",
            className: cn("shrink-0", tone === "accent" && "text-accent")
          }
        ),
        labelling === "adjacent" ? null : /* @__PURE__ */ jsx29(
          "span",
          {
            className: cn(
              labelling === "assistive" && "sr-only",
              // The glyph carries the accent; printed words stay in the page's
              // own voice, so "AI generated" beside a heading is not a second
              // shout. On a filled control they take the fill's foreground.
              labelling === "visible" && tone === "accent" && "text-text-muted"
            ),
            children: label ?? AI_MARKER_WORD[marks]
          }
        )
      ]
    }
  )
);
AiMarker.displayName = "AiMarker";

// src/components/machine-generated.tsx
import { jsx as jsx30, jsxs as jsxs26 } from "react/jsx-runtime";
var MachineGenerated = React29.forwardRef(
  ({ label = "AI generated", className, children, ...props }, ref) => /* @__PURE__ */ jsxs26(
    "div",
    {
      ref,
      "data-machine-generated": "",
      className: cn("flex min-w-0 flex-col gap-2 border-l-2 border-l-accent pl-3", className),
      ...props,
      children: [
        /* @__PURE__ */ jsxs26("span", { className: "eyebrow inline-flex items-center gap-1 self-start rounded-sm bg-accent-subtle px-1.5", children: [
          /* @__PURE__ */ jsx30(AiMarker, { labelling: "adjacent" }),
          label
        ] }),
        /* @__PURE__ */ jsx30("div", { className: "min-w-0 text-body text-text", children })
      ]
    }
  )
);
MachineGenerated.displayName = "MachineGenerated";

// src/components/empty-state.tsx
import * as React30 from "react";
import { jsx as jsx31, jsxs as jsxs27 } from "react/jsx-runtime";
var EmptyState = React30.forwardRef(
  ({ title, description, level = 2, className, children, ...props }, ref) => {
    const Heading = `h${level}`;
    return /* @__PURE__ */ jsxs27(
      "div",
      {
        ref,
        className: cn("flex flex-col items-center gap-2 text-center", className),
        ...props,
        children: [
          /* @__PURE__ */ jsx31(Heading, { className: "m-0 text-h2 text-text", children: title }),
          description ? /* @__PURE__ */ jsx31("p", { className: "max-w-md text-body-sm text-text-muted", children: description }) : null,
          children ? /* @__PURE__ */ jsx31("div", { className: "mt-3 flex items-center gap-3", children }) : null
        ]
      }
    );
  }
);
EmptyState.displayName = "EmptyState";

// src/components/page-header.tsx
import * as React31 from "react";
import { jsx as jsx32, jsxs as jsxs28 } from "react/jsx-runtime";
var PageHeader = React31.forwardRef(
  ({ breadcrumb, title, description, actions, className, children, ...props }, ref) => /* @__PURE__ */ jsxs28("header", { ref, className: cn("flex min-w-0 flex-col gap-2", className), ...props, children: [
    breadcrumb ? /* @__PURE__ */ jsx32("div", { className: "text-body-sm text-text-muted", children: breadcrumb }) : null,
    /* @__PURE__ */ jsxs28("div", { className: "flex items-start justify-between gap-6", children: [
      /* @__PURE__ */ jsxs28("div", { className: "flex min-w-0 flex-col gap-1", children: [
        /* @__PURE__ */ jsx32("h1", { className: "m-0 text-h1 text-text", children: title }),
        description ? /* @__PURE__ */ jsx32("p", { className: "text-body text-text-muted", children: description }) : null
      ] }),
      actions ? /* @__PURE__ */ jsx32("div", { className: "flex shrink-0 items-center gap-3", children: actions }) : null
    ] }),
    children
  ] })
);
PageHeader.displayName = "PageHeader";

// src/components/data-table.tsx
import {
  columnVisibilityFeature,
  createPaginatedRowModel,
  createSortedRowModel,
  rowPaginationFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
  useTable
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown, Columns3 } from "lucide-react";
import * as React32 from "react";
import { Fragment as Fragment11, jsx as jsx33, jsxs as jsxs29 } from "react/jsx-runtime";
var features = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  rowPaginationFeature,
  paginatedRowModel: createPaginatedRowModel(),
  // Visibility is TanStack's: it already keeps a hidden column's definition
  // alive, which is what lets a sort survive its own column being hidden.
  columnVisibilityFeature,
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    text: sortFn_text
  }
});
var DEFAULT_PAGE_SIZES = [25, 50, 100];
var KIND_CELL = {
  text: "text-left text-text",
  secondary: "text-left text-text-muted",
  numeric: "text-right font-mono whitespace-nowrap text-text",
  date: "text-left font-mono whitespace-nowrap text-text-muted",
  duration: "text-right font-mono whitespace-nowrap text-text",
  status: "text-left whitespace-nowrap text-text",
  action: "text-right w-px whitespace-nowrap"
};
var KIND_HEAD = {
  text: "text-left",
  secondary: "text-left",
  numeric: "text-right whitespace-nowrap",
  date: "text-left whitespace-nowrap",
  duration: "text-right whitespace-nowrap",
  status: "text-left whitespace-nowrap",
  action: "text-right w-px whitespace-nowrap print:hidden"
};
var ACTION_COLUMN_ID = "__actions";
function isModified(e) {
  return e.metaKey || e.ctrlKey;
}
function rangeBetween(order, a, b) {
  const ia = order.indexOf(a);
  const ib = order.indexOf(b);
  if (ia === -1 || ib === -1) return [b];
  const [from, to] = ia < ib ? [ia, ib] : [ib, ia];
  return order.slice(from, to + 1);
}
function columnIdOf(column) {
  const def = column;
  if (def.id !== void 0) return String(def.id);
  if (def.accessorKey !== void 0) return String(def.accessorKey).split(".").join("_");
  return typeof def.header === "string" ? def.header : void 0;
}
function columnLabelOf(meta, header, id) {
  if (meta?.label) return meta.label;
  return typeof header === "string" ? header : id;
}
function sanitiseColumnVisibility(value, columns) {
  if (!value) return null;
  const next = {};
  let shown = 0;
  for (const column of columns) {
    if (column.canHide && value[column.id] === false) next[column.id] = false;
    else if (column.content) shown += 1;
  }
  return shown === 0 ? null : next;
}
var STORAGE_PREFIX = "profylr.ui.data-table.";
var STORAGE_SUFFIX = ".columns";
function columnVisibilityStorageKey(tableId) {
  return `${STORAGE_PREFIX}${tableId}${STORAGE_SUFFIX}`;
}
function storage() {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage ?? null;
  } catch {
    return null;
  }
}
function loadColumnVisibility(tableId) {
  const store = storage();
  if (!store) return null;
  let raw;
  try {
    raw = store.getItem(columnVisibilityStorageKey(tableId));
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return null;
  const value = {};
  for (const [id, visible] of Object.entries(parsed)) {
    if (typeof visible === "boolean") value[id] = visible;
  }
  return value;
}
function saveColumnVisibility(tableId, value) {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(columnVisibilityStorageKey(tableId), JSON.stringify(value));
  } catch {
  }
}
function clearColumnVisibility(tableId) {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(columnVisibilityStorageKey(tableId));
  } catch {
  }
}
function DataTable({
  columns,
  data,
  getRowId,
  caption,
  density,
  selection,
  sorting: sortingProp,
  onSortChange,
  columnVisibility: columnVisibilityProp,
  onColumnVisibilityChange,
  defaultColumnVisibility,
  tableId,
  columnChooser,
  scrollable = true,
  onRowOpen,
  rowActions,
  pagination,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  state = "ready",
  emptyState,
  filteredEmptyState,
  filtered = false,
  errorState,
  onRetry,
  toolbar,
  bulkActions,
  className
}) {
  const [internalSort, setInternalSort] = React32.useState(null);
  const sort = sortingProp !== void 0 ? sortingProp : internalSort;
  const setSort = (next) => {
    if (sortingProp === void 0) setInternalSort(next);
    onSortChange?.(next);
  };
  const sortingState = React32.useMemo(
    () => sort ? [{ id: sort.columnId, desc: sort.direction === "desc" }] : [],
    [sort]
  );
  const manual = pagination?.total !== void 0;
  const paginationState = React32.useMemo(
    () => ({
      pageIndex: pagination ? pagination.page - 1 : 0,
      pageSize: pagination ? pagination.pageSize : Math.max(data.length, 1)
    }),
    [pagination, data.length]
  );
  const allColumns = React32.useMemo(() => {
    const cols = columns.map(
      (column, index) => index === 0 && column.enableHiding === void 0 ? { ...column, enableHiding: false } : column
    );
    if (rowActions) {
      cols.push({
        id: ACTION_COLUMN_ID,
        header: () => /* @__PURE__ */ jsx33("span", { className: "sr-only", children: "Actions" }),
        cell: ({ row }) => rowActions(row.original),
        enableSorting: false,
        enableHiding: false,
        meta: { kind: "action", label: "Actions" }
      });
    }
    return cols;
  }, [columns, rowActions]);
  const columnIndex = React32.useMemo(() => {
    const index = [];
    for (const column of allColumns) {
      const id = columnIdOf(column);
      if (id === void 0) continue;
      const meta = column.meta;
      index.push({
        id,
        label: columnLabelOf(meta, column.header, id),
        canHide: column.enableHiding !== false,
        content: (meta?.kind ?? "text") !== "action"
      });
    }
    return index;
  }, [allColumns]);
  const [rawVisibility, setRawVisibility] = React32.useState(
    () => (tableId ? loadColumnVisibility(tableId) : null) ?? defaultColumnVisibility ?? {}
  );
  const internalVisibility = React32.useMemo(
    () => sanitiseColumnVisibility(rawVisibility, columnIndex) ?? {},
    [rawVisibility, columnIndex]
  );
  const defaultsRef = React32.useRef(defaultColumnVisibility);
  defaultsRef.current = defaultColumnVisibility;
  const lastTableId = React32.useRef(tableId);
  React32.useEffect(() => {
    if (lastTableId.current === tableId) return;
    lastTableId.current = tableId;
    setRawVisibility((tableId ? loadColumnVisibility(tableId) : null) ?? defaultsRef.current ?? {});
  }, [tableId]);
  const requestedVisibility = columnVisibilityProp !== void 0 ? columnVisibilityProp : internalVisibility;
  const columnVisibility = columnIndex.some(
    (column) => column.content && requestedVisibility[column.id] !== false
  ) ? requestedVisibility : {};
  const applyVisibility = (next) => {
    if (columnVisibilityProp === void 0) {
      setRawVisibility(next);
      if (tableId) saveColumnVisibility(tableId, next);
    }
    onColumnVisibilityChange?.(next);
  };
  const [columnAnnouncement, setColumnAnnouncement] = React32.useState("");
  const shownLabels = React32.useMemo(
    () => columnIndex.filter((column) => columnVisibility[column.id] !== false).map((column) => column.label),
    [columnIndex, columnVisibility]
  );
  const contentCount = columnIndex.length;
  const shownKey = shownLabels.join("\0");
  const announcedKey = React32.useRef(null);
  React32.useEffect(() => {
    const previous = announcedKey.current;
    announcedKey.current = shownKey;
    if (previous === null) return;
    const before = previous === "" ? [] : previous.split("\0");
    const after = shownLabels;
    const gone = before.filter((label) => !after.includes(label));
    const back = after.filter((label) => !before.includes(label));
    const count2 = `${after.length} of ${contentCount} columns shown.`;
    if (gone.length === 0 && back.length > 1 && after.length === contentCount)
      setColumnAnnouncement("All columns shown.");
    else if (gone.length === 1 && back.length === 0)
      setColumnAnnouncement(`${gone[0]} hidden. ${count2}`);
    else if (back.length === 1 && gone.length === 0)
      setColumnAnnouncement(`${back[0]} shown. ${count2}`);
    else if (gone.length || back.length) setColumnAnnouncement(count2);
  }, [shownKey, contentCount]);
  const table = useTable({
    features,
    columns: allColumns,
    data,
    getRowId,
    state: { sorting: sortingState, pagination: paginationState, columnVisibility },
    manualSorting: false,
    manualPagination: manual,
    rowCount: manual ? pagination?.total : void 0,
    enableSortingRemoval: true,
    autoResetPageIndex: false
  });
  const rows = table.getRowModel().rows;
  const renderedIds = React32.useMemo(() => rows.map((r) => r.id), [rows]);
  const total = manual ? pagination?.total ?? data.length : data.length;
  const anchor = React32.useRef(null);
  const selected = selection?.selectedIds ?? EMPTY_SET;
  const commit = (next) => selection?.onChange(next);
  const handleRowClick = (id, event, fromBody, row) => {
    if (!selection) {
      if (fromBody && !isModified(event) && !event.shiftKey) onRowOpen?.(row);
      return;
    }
    const mod = isModified(event);
    if (event.shiftKey) {
      const from = anchor.current ?? id;
      const range = rangeBetween(renderedIds, from, id);
      if (mod) {
        const next = new Set(selected);
        for (const r of range) next.add(r);
        commit(next);
      } else {
        commit(new Set(range));
      }
      if (anchor.current === null) anchor.current = id;
      return;
    }
    if (mod || !fromBody) {
      const next = new Set(selected);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      commit(next);
      anchor.current = id;
      return;
    }
    onRowOpen?.(row);
  };
  const handleMouseDown = (event) => {
    if (event.shiftKey && selection) {
      event.preventDefault();
      window.getSelection?.()?.removeAllRanges();
    }
  };
  const clearSelection = () => {
    if (selection && selected.size > 0) commit(/* @__PURE__ */ new Set());
    anchor.current = null;
  };
  const pageIds = renderedIds;
  const pageSelectedCount = pageIds.filter((id) => selected.has(id)).length;
  const allPageSelected = pageIds.length > 0 && pageSelectedCount === pageIds.length;
  const somePageSelected = pageSelectedCount > 0 && !allPageSelected;
  const togglePage = () => {
    if (!selection) return;
    const next = new Set(selected);
    if (allPageSelected) for (const id of pageIds) next.delete(id);
    else for (const id of pageIds) next.add(id);
    commit(next);
  };
  const [focusIndex, setFocusIndex] = React32.useState(0);
  const rowRefs = React32.useRef([]);
  React32.useEffect(() => {
    if (focusIndex > rows.length - 1) setFocusIndex(Math.max(0, rows.length - 1));
  }, [rows.length, focusIndex]);
  const focusRow = (index) => {
    const clamped = Math.max(0, Math.min(rows.length - 1, index));
    setFocusIndex(clamped);
    rowRefs.current[clamped]?.focus();
    return clamped;
  };
  const handleRowKeyDown = (event, index) => {
    const row = rows[index];
    if (!row) return;
    const id = row.id;
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        event.preventDefault();
        const next = focusRow(index + (event.key === "ArrowDown" ? 1 : -1));
        if (event.shiftKey && selection) {
          const from = anchor.current ?? id;
          commit(new Set(rangeBetween(renderedIds, from, renderedIds[next])));
          if (anchor.current === null) anchor.current = id;
        }
        return;
      }
      case " ": {
        if (!selection) return;
        event.preventDefault();
        if (event.shiftKey) {
          const from = anchor.current ?? id;
          commit(new Set(rangeBetween(renderedIds, from, id)));
          if (anchor.current === null) anchor.current = id;
        } else {
          const next = new Set(selected);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          commit(next);
          anchor.current = id;
        }
        return;
      }
      case "Enter": {
        if (event.target !== event.currentTarget) return;
        event.preventDefault();
        onRowOpen?.(row.original);
        return;
      }
      case "Escape": {
        if (!selection) return;
        event.preventDefault();
        clearSelection();
        return;
      }
      case "a":
      case "A": {
        if (selection && isModified(event)) {
          event.preventDefault();
          const next = new Set(selected);
          for (const r of pageIds) next.add(r);
          commit(next);
        }
        return;
      }
      default:
    }
  };
  const cycle = (columnId) => {
    if (!sort || sort.columnId !== columnId) return setSort({ columnId, direction: "asc" });
    if (sort.direction === "asc") return setSort({ columnId, direction: "desc" });
    return setSort(null);
  };
  const sortedColumnLabel = React32.useMemo(() => {
    if (!sort) return null;
    return columnIndex.find((c) => c.id === sort.columnId)?.label ?? sort.columnId;
  }, [sort, columnIndex]);
  const chooserColumns = table.getAllLeafColumns().map((column) => ({
    id: column.id,
    label: columnLabelOf(
      column.columnDef.meta,
      column.columnDef.header,
      column.id
    ),
    canHide: column.getCanHide(),
    visible: column.getIsVisible(),
    content: column.id !== ACTION_COLUMN_ID
  }));
  const shownCount = chooserColumns.filter((column) => column.visible).length;
  const shownDataCount = chooserColumns.filter((column) => column.visible && column.content).length;
  const showChooser = (columnChooser ?? Boolean(
    tableId || columnVisibilityProp !== void 0 || onColumnVisibilityChange || defaultColumnVisibility
  )) && chooserColumns.some((column) => column.canHide);
  const toggleColumn = (id, visible) => {
    const next = { ...requestedVisibility };
    if (visible) delete next[id];
    else next[id] = false;
    applyVisibility(next);
  };
  const showAllColumns = () => applyVisibility({});
  const sortedColumnHidden = Boolean(
    sort && chooserColumns.some((column) => column.id === sort.columnId && !column.visible)
  );
  const showSortedColumnHiddenNote = sortedColumnHidden && showChooser;
  const pageSize = pagination?.pageSize ?? total;
  const first = total === 0 ? 0 : (pagination ? (pagination.page - 1) * pageSize : 0) + 1;
  const last = total === 0 ? 0 : Math.min(total, first + rows.length - 1);
  const pageCount = pagination ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const columnCount = table.getVisibleLeafColumns().length + (selection ? 1 : 0);
  return /* @__PURE__ */ jsxs29("div", { className: cn("flex flex-col gap-3", className), "data-density": density, children: [
    toolbar || showChooser || showSortedColumnHiddenNote ? /* @__PURE__ */ jsxs29("div", { className: "flex flex-wrap items-center gap-3", children: [
      toolbar,
      showChooser || showSortedColumnHiddenNote ? /* @__PURE__ */ jsxs29("div", { className: "ml-auto flex flex-wrap items-center gap-3 print:hidden", children: [
        showSortedColumnHiddenNote ? /* @__PURE__ */ jsxs29("p", { className: "text-caption text-text-muted", children: [
          "Sorted by ",
          sortedColumnLabel,
          ",",
          " ",
          sort?.direction === "desc" ? "descending" : "ascending",
          " \u2014 column hidden"
        ] }) : null,
        showChooser ? /* @__PURE__ */ jsx33(
          ColumnChooser,
          {
            columns: chooserColumns,
            shownCount,
            shownDataCount,
            onToggle: toggleColumn,
            onShowAll: showAllColumns
          }
        ) : null
      ] }) : null
    ] }) : null,
    /* @__PURE__ */ jsx33("p", { role: "status", className: "sr-only", children: columnAnnouncement }),
    /* @__PURE__ */ jsx33(TableScroller, { enabled: scrollable, label: caption, children: /* @__PURE__ */ jsxs29(
      "table",
      {
        className: "w-full border-collapse text-body-sm",
        style: { tableLayout: "auto" },
        "aria-busy": state === "loading" || void 0,
        onMouseDown: handleMouseDown,
        children: [
          /* @__PURE__ */ jsxs29("caption", { className: "sr-only print:not-sr-only print:text-left print:text-caption print:text-text-muted", children: [
            caption,
            sortedColumnLabel ? /* @__PURE__ */ jsxs29("span", { className: "sr-only print:not-sr-only", children: [
              " ",
              "\u2014 sorted by ",
              sortedColumnLabel,
              ", ",
              sort?.direction === "desc" ? "descending" : "ascending"
            ] }) : null
          ] }),
          /* @__PURE__ */ jsx33("thead", { className: "bg-surface", children: table.getHeaderGroups().map((group) => /* @__PURE__ */ jsxs29("tr", { className: "border-b border-divider", children: [
            selection ? /* @__PURE__ */ jsx33("th", { scope: "col", className: "w-px p-cell first:pl-0 print:hidden", children: /* @__PURE__ */ jsx33(
              Checkbox,
              {
                "aria-label": allPageSelected ? "Deselect all rows on this page" : "Select all rows on this page",
                label: /* @__PURE__ */ jsx33("span", { className: "sr-only", children: "Select page" }),
                checked: allPageSelected ? true : somePageSelected ? "indeterminate" : false,
                onCheckedChange: togglePage
              }
            ) }) : null,
            group.headers.map((header) => {
              const meta = header.column.columnDef.meta ?? {};
              const kind = meta.kind ?? "text";
              const canSort = header.column.getCanSort();
              const isSorted = sort?.columnId === header.column.id ? sort.direction : null;
              const ariaSort = isSorted === "asc" ? "ascending" : isSorted === "desc" ? "descending" : canSort ? "none" : void 0;
              return /* @__PURE__ */ jsx33(
                "th",
                {
                  scope: "col",
                  "aria-sort": ariaSort,
                  className: cn("p-cell first:pl-0 last:pr-0 align-bottom", KIND_HEAD[kind]),
                  children: header.isPlaceholder ? null : canSort ? (
                    // A real button inside the th, never a click handler on
                    // the th: 24px target, visible focus, keyboard operable.
                    /* @__PURE__ */ jsxs29(
                      "button",
                      {
                        type: "button",
                        onClick: () => cycle(header.column.id),
                        className: cn(
                          "table-head inline-flex min-h-6 items-center gap-1 rounded-sm text-text-muted",
                          "hover:text-text transition-colors duration-fast ease-move",
                          // The indicator always trails the label, on every
                          // column kind. It used to lead on right-aligned ones
                          // so it sat beside the label rather than against the
                          // table edge -- locally the better of the two, and
                          // wrong across a row: a header strip that put the
                          // caret after AREA and STATUS and before COMPONENTS
                          // and HEALTHY read as broken rather than as tuned,
                          // because the eye reads the row before it reads any
                          // one column. One side, everywhere.
                          FOCUS_RING
                        ),
                        children: [
                          /* @__PURE__ */ jsx33(table.FlexRender, { header }),
                          /* @__PURE__ */ jsx33(SortGlyph, { direction: isSorted })
                        ]
                      }
                    )
                  ) : /* @__PURE__ */ jsx33("span", { className: "table-head text-text-muted", children: /* @__PURE__ */ jsx33(table.FlexRender, { header }) })
                },
                header.id
              );
            })
          ] }, group.id)) }),
          /* @__PURE__ */ jsx33("tbody", { children: state === "loading" ? /* @__PURE__ */ jsx33(SkeletonRows, { count: pagination?.pageSize ?? 8, columns: columnCount }) : state === "error" ? /* @__PURE__ */ jsx33("tr", { children: /* @__PURE__ */ jsx33("td", { colSpan: columnCount, className: "p-cell py-8 text-center", children: /* @__PURE__ */ jsxs29("div", { className: "flex flex-col items-center gap-3", children: [
            /* @__PURE__ */ jsx33("p", { className: "text-body text-text", children: errorState ?? "Something went wrong loading this list." }),
            onRetry ? /* @__PURE__ */ jsx33(Button, { variant: "secondary", size: "sm", onClick: onRetry, children: "Retry" }) : null
          ] }) }) }) : rows.length === 0 ? /* @__PURE__ */ jsx33("tr", { children: /* @__PURE__ */ jsx33("td", { colSpan: columnCount, className: "p-cell py-8", children: filtered ? filteredEmptyState : emptyState }) }) : rows.map((row, index) => {
            const id = row.id;
            const isSelected = selected.has(id);
            return /* @__PURE__ */ jsxs29(
              "tr",
              {
                ref: (el) => {
                  rowRefs.current[index] = el;
                },
                tabIndex: index === focusIndex ? 0 : -1,
                "aria-selected": selection ? isSelected : void 0,
                "data-selected": isSelected || void 0,
                onKeyDown: (e) => handleRowKeyDown(e, index),
                onFocus: () => setFocusIndex(index),
                onClick: (e) => handleRowClick(id, e, true, row.original),
                className: cn(
                  "border-b border-divider transition-colors duration-fast ease-move",
                  // Sticky header must not obscure a focused row (2.4.11).
                  "scroll-mt-16",
                  "hover:bg-surface-raised print:hover:bg-transparent",
                  isSelected && "bg-surface-raised print:bg-transparent",
                  (onRowOpen || selection) && "cursor-pointer",
                  FOCUS_RING_WITHIN
                ),
                children: [
                  selection ? /* @__PURE__ */ jsx33(
                    "td",
                    {
                      className: cn(
                        "w-px p-cell first:pl-0 print:hidden border-l-2",
                        // The 2px accent bar, on the first cell only.
                        isSelected ? "border-l-accent" : "border-l-transparent"
                      ),
                      onClick: (e) => e.stopPropagation(),
                      children: /* @__PURE__ */ jsx33(
                        Checkbox,
                        {
                          label: /* @__PURE__ */ jsx33("span", { className: "sr-only", children: "Select row" }),
                          checked: isSelected,
                          onClick: (e) => handleRowClick(id, e, false, row.original),
                          onCheckedChange: () => {
                          }
                        }
                      )
                    }
                  ) : null,
                  row.getVisibleCells().map((cell, cellIndex) => {
                    const meta = cell.column.columnDef.meta ?? {};
                    const kind = meta.kind ?? "text";
                    const firstCell = !selection && cellIndex === 0;
                    return /* @__PURE__ */ jsx33(
                      "td",
                      {
                        "data-numeric": kind === "numeric" || kind === "duration" ? "" : void 0,
                        onClick: kind === "action" ? (e) => e.stopPropagation() : void 0,
                        className: cn(
                          "p-cell first:pl-0 last:pr-0 align-middle",
                          KIND_CELL[kind],
                          firstCell && "border-l-2",
                          firstCell && (isSelected ? "border-l-accent" : "border-l-transparent"),
                          kind === "action" && "print:hidden",
                          meta.cellClassName
                        ),
                        children: /* @__PURE__ */ jsx33(table.FlexRender, { cell })
                      },
                      cell.id
                    );
                  })
                ]
              },
              id
            );
          }) })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx33(
      DataTableFooter,
      {
        selection,
        selectedCount: selected.size,
        allPageSelected,
        pageSize,
        total,
        first,
        last,
        page: pagination?.page ?? 1,
        pageCount,
        pagination,
        pageSizeOptions,
        bulkActions,
        selected
      }
    )
  ] });
}
var EMPTY_SET = /* @__PURE__ */ new Set();
function TableScroller({
  enabled,
  label,
  children
}) {
  if (!enabled) return /* @__PURE__ */ jsx33(Fragment11, { children });
  return /* @__PURE__ */ jsx33(
    "div",
    {
      role: "group",
      "aria-label": label,
      tabIndex: 0,
      className: cn("-m-1 overflow-x-auto p-1 print:overflow-x-visible", FOCUS_RING),
      children
    }
  );
}
function ColumnChooser({
  columns,
  shownCount,
  shownDataCount,
  onToggle,
  onShowAll
}) {
  const hiddenCount = columns.length - shownCount;
  return /* @__PURE__ */ jsxs29(Popover, { children: [
    /* @__PURE__ */ jsx33(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs29(
      Button,
      {
        variant: "secondary",
        size: "sm",
        "aria-label": hiddenCount > 0 ? `Columns ${shownCount}/${columns.length} shown` : "Columns",
        children: [
          /* @__PURE__ */ jsx33(Columns3, { "aria-hidden": "true" }),
          /* @__PURE__ */ jsx33("span", { children: "Columns" }),
          hiddenCount > 0 ? /* @__PURE__ */ jsxs29("span", { className: "font-mono", "data-numeric": true, children: [
            shownCount,
            "/",
            columns.length
          ] }) : null
        ]
      }
    ) }),
    /* @__PURE__ */ jsxs29(PopoverContent, { "aria-label": "Columns", align: "end", className: "max-h-96 overflow-y-auto", children: [
      /* @__PURE__ */ jsxs29("fieldset", { className: "flex min-w-0 flex-col gap-2", children: [
        /* @__PURE__ */ jsx33("legend", { className: "eyebrow mb-2", children: "Columns shown" }),
        columns.map((column) => {
          const last = column.visible && column.content && shownDataCount === 1;
          return /* @__PURE__ */ jsx33(
            Checkbox,
            {
              checked: column.visible,
              disabled: !column.canHide || last,
              onCheckedChange: (next) => onToggle(column.id, next === true),
              label: column.label,
              description: !column.canHide ? "Always shown" : last ? "The last column shown cannot be hidden" : void 0
            },
            column.id
          );
        })
      ] }),
      hiddenCount > 0 ? /* @__PURE__ */ jsx33("div", { className: "mt-3 border-t border-divider pt-3", children: /* @__PURE__ */ jsx33(Button, { variant: "link", size: "sm", onClick: onShowAll, children: "Show all columns" }) }) : null
    ] })
  ] });
}
function truncateCell(value, options) {
  return /* @__PURE__ */ jsx33("span", { className: cn("block max-w-48 truncate", options?.className), title: value, children: value });
}
function SortGlyph({ direction }) {
  const Icon2 = direction === "asc" ? ArrowUp : direction === "desc" ? ArrowDown : ChevronsUpDown;
  return /* @__PURE__ */ jsx33(
    Icon2,
    {
      className: cn("size-4 shrink-0", direction ? "text-text" : "text-text-muted"),
      "aria-hidden": "true"
    }
  );
}
function SkeletonRows({ count: count2, columns }) {
  return /* @__PURE__ */ jsx33(Fragment11, { children: Array.from({ length: Math.min(count2, 25) }).map((_, i) => /* @__PURE__ */ jsx33("tr", { className: "border-b border-divider", "aria-hidden": "true", children: Array.from({ length: columns }).map((__, j) => /* @__PURE__ */ jsx33("td", { className: "p-cell", children: /* @__PURE__ */ jsx33("span", { className: "block h-4 w-3/4 animate-pulse rounded-sm bg-surface-sunken" }) }, j)) }, i)) });
}
function DataTableFooter({
  selection,
  selectedCount,
  allPageSelected,
  pageSize,
  total,
  first,
  last,
  page,
  pageCount,
  pagination,
  pageSizeOptions,
  bulkActions,
  selected
}) {
  const acrossPages = selection?.onSelectAllAcrossPages && selection.totalCount !== void 0 && allPageSelected && selectedCount < selection.totalCount;
  return /* @__PURE__ */ jsxs29("div", { className: "flex flex-wrap items-center justify-between gap-3 print:hidden", children: [
    /* @__PURE__ */ jsxs29("div", { className: "flex min-w-0 flex-wrap items-center gap-3", children: [
      selection ? /* @__PURE__ */ jsx33("p", { className: "font-mono text-caption text-text-muted", "data-numeric": true, "aria-live": "polite", "aria-atomic": "true", children: selectedCount === 0 ? "None selected" : `${selectedCount} selected${selection.totalCount !== void 0 ? ` of ${selection.totalCount}` : ""}` }) : null,
      acrossPages ? /* @__PURE__ */ jsxs29(Button, { variant: "link", size: "sm", onClick: selection?.onSelectAllAcrossPages, children: [
        "Select all ",
        selection?.totalCount
      ] }) : null,
      bulkActions && selectedCount > 0 ? /* @__PURE__ */ jsx33("div", { className: "flex items-center gap-2", children: bulkActions(selected) }) : null
    ] }),
    pagination ? /* @__PURE__ */ jsxs29("div", { className: "flex items-center gap-4", children: [
      /* @__PURE__ */ jsxs29("p", { className: "font-mono text-caption text-text-muted", "data-numeric": true, children: [
        first,
        "\u2013",
        last,
        " of ",
        total
      ] }),
      /* @__PURE__ */ jsxs29(
        Select,
        {
          value: String(pageSize),
          onValueChange: (v) => pagination.onChange(1, Number(v)),
          children: [
            /* @__PURE__ */ jsx33(SelectTrigger, { "aria-label": "Rows per page", className: "h-8 w-auto gap-1 text-body-sm", children: /* @__PURE__ */ jsx33(SelectValue, {}) }),
            /* @__PURE__ */ jsx33(SelectContent, { children: pageSizeOptions.map((size) => /* @__PURE__ */ jsxs29(SelectItem, { value: String(size), children: [
              size,
              " per page"
            ] }, size)) })
          ]
        }
      ),
      /* @__PURE__ */ jsxs29("div", { className: "flex items-center gap-1", children: [
        /* @__PURE__ */ jsx33(
          Button,
          {
            variant: "ghost",
            size: "sm",
            onClick: () => pagination.onChange(page - 1, pageSize),
            disabled: page <= 1,
            children: "Previous"
          }
        ),
        /* @__PURE__ */ jsx33(
          Button,
          {
            variant: "ghost",
            size: "sm",
            onClick: () => pagination.onChange(page + 1, pageSize),
            disabled: page >= pageCount,
            children: "Next"
          }
        )
      ] })
    ] }) : null
  ] });
}

// src/components/chart.tsx
import * as React33 from "react";
import {
  Area,
  AreaChart as RechartsAreaChart,
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart as RechartsLineChart,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
  Tooltip as Tooltip2,
  XAxis,
  YAxis
} from "recharts";
import { Fragment as Fragment12, jsx as jsx34, jsxs as jsxs30 } from "react/jsx-runtime";
var CHART_SERIES_VARS = [
  "var(--profylr-chart-1)",
  "var(--profylr-chart-2)",
  "var(--profylr-chart-3)",
  "var(--profylr-chart-4)",
  "var(--profylr-chart-5)",
  "var(--profylr-chart-6)",
  "var(--profylr-chart-7)",
  "var(--profylr-chart-8)"
];
function chartSeriesVar(index) {
  return CHART_SERIES_VARS[(index % CHART_SERIES_VARS.length + CHART_SERIES_VARS.length) % CHART_SERIES_VARS.length];
}
var CHART_TONE_VARS = {
  compliant: "var(--profylr-status-compliant)",
  warning: "var(--profylr-status-warning)",
  degraded: "var(--profylr-status-degraded)",
  breach: "var(--profylr-status-breach)",
  "not-assessed": "var(--profylr-status-not-assessed)"
};
var GRID_VAR = "var(--profylr-chart-grid)";
var MUTED_VAR = "var(--profylr-text-muted)";
var SURFACE_VAR = "var(--profylr-surface-raised)";
var BORDER_VAR = "var(--profylr-border)";
var TEXT_VAR = "var(--profylr-text)";
function seriesColour(series, index) {
  if (series.colour) return series.colour;
  if (series.tone) return CHART_TONE_VARS[series.tone];
  return chartSeriesVar(index);
}
function defaultFormat(value) {
  return Number.isInteger(value) ? String(value) : String(Number.parseFloat(value.toPrecision(6)));
}
function figuresText(data, series, categoryKey, format) {
  if (data.length === 0) return "No data.";
  const rows = data.map((datum) => {
    const category = String(datum[categoryKey] ?? "");
    const values = series.map((s) => {
      const raw = datum[s.key];
      const figure = typeof raw === "number" && Number.isFinite(raw) ? format(raw) : "no reading";
      return series.length === 1 ? figure : `${s.label} ${figure}`;
    }).join(", ");
    return category ? `${category}: ${values}` : values;
  });
  return `Figures: ${rows.join("; ")}.`;
}
function describe(data, series, categoryKey, format) {
  if (data.length === 0) return "no data.";
  const parts = series.map((s) => {
    let low = null;
    let high = null;
    let lowAt = "";
    let highAt = "";
    let total = 0;
    let count2 = 0;
    for (const datum of data) {
      const raw = datum[s.key];
      if (typeof raw !== "number" || !Number.isFinite(raw)) continue;
      const at = String(datum[categoryKey] ?? "");
      total += raw;
      count2 += 1;
      if (low === null || raw < low) {
        low = raw;
        lowAt = at;
      }
      if (high === null || raw > high) {
        high = raw;
        highAt = at;
      }
    }
    if (count2 === 0) return `${s.label}: no readings`;
    if (low === high) return `${s.label}: flat at ${format(low)} across ${count2} readings`;
    return `${s.label}: ${count2} readings totalling ${format(total)}, highest ${format(high)}${highAt ? ` at ${highAt}` : ""}, lowest ${format(low)}${lowAt ? ` at ${lowAt}` : ""}`;
  });
  return `${parts.join(". ")}.`;
}
var ChartFrame = React33.forwardRef(
  ({ label, summary, figures, height, decorative = false, children, className, ...props }, ref) => {
    const generated = React33.useId();
    const figuresId = `chart-${generated}-figures`;
    return /* @__PURE__ */ jsxs30(
      "div",
      {
        ref,
        role: decorative ? void 0 : "img",
        "aria-hidden": decorative ? true : void 0,
        "aria-label": decorative ? void 0 : `${label}: ${summary}`,
        "aria-describedby": decorative ? void 0 : figuresId,
        className: cn("w-full", className),
        ...props,
        children: [
          decorative ? null : /* @__PURE__ */ jsx34("p", { id: figuresId, className: "sr-only", children: figures }),
          /* @__PURE__ */ jsx34("div", { "aria-hidden": "true", style: { height }, children: /* @__PURE__ */ jsx34(ResponsiveContainer, { width: "100%", height: "100%", children }) })
        ]
      }
    );
  }
);
ChartFrame.displayName = "ChartFrame";
function ChartTooltip({ active, payload, label, format = defaultFormat }) {
  if (!active || !payload || payload.length === 0) return null;
  return /* @__PURE__ */ jsxs30(
    "div",
    {
      className: "rounded-none border border-border bg-surface-raised px-3 py-2 text-sm",
      style: { background: SURFACE_VAR, borderColor: BORDER_VAR, color: TEXT_VAR },
      children: [
        label !== void 0 && label !== "" ? /* @__PURE__ */ jsx34("p", { className: "mb-1 font-medium", style: { color: TEXT_VAR }, children: label }) : null,
        /* @__PURE__ */ jsx34("ul", { className: "space-y-0.5", children: payload.map((entry, index) => /* @__PURE__ */ jsxs30("li", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx34(
            "span",
            {
              "aria-hidden": "true",
              className: "inline-block size-2 shrink-0",
              style: { background: entry.color }
            }
          ),
          /* @__PURE__ */ jsx34("span", { style: { color: MUTED_VAR }, children: entry.name }),
          /* @__PURE__ */ jsx34("span", { className: "ml-auto font-mono tabular-nums", style: { color: TEXT_VAR }, children: typeof entry.value === "number" ? format(entry.value) : entry.value })
        ] }, `${entry.dataKey ?? entry.name ?? index}`)) })
      ]
    }
  );
}
function ChartLegend({ series, className, ...props }) {
  return /* @__PURE__ */ jsx34(
    "ul",
    {
      "aria-hidden": "true",
      className: cn("mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm", className),
      style: { color: MUTED_VAR },
      ...props,
      children: series.map((s, index) => /* @__PURE__ */ jsxs30("li", { className: "flex items-center gap-1.5", children: [
        /* @__PURE__ */ jsx34(
          "span",
          {
            className: "inline-block size-2 shrink-0",
            style: { background: seriesColour(s, index) }
          }
        ),
        s.label
      ] }, s.key))
    }
  );
}
var AXIS_PROPS = {
  stroke: MUTED_VAR,
  tick: { fill: MUTED_VAR, fontSize: 12 },
  tickLine: false,
  axisLine: { stroke: GRID_VAR }
};
function CartesianFurniture({
  categoryKey,
  format,
  hideValueAxis,
  hideGrid,
  rightAxis,
  horizontal,
  categoryWidth
}) {
  if (horizontal) {
    return /* @__PURE__ */ jsxs30(Fragment12, { children: [
      hideGrid ? null : /* @__PURE__ */ jsx34(CartesianGrid, { stroke: GRID_VAR, strokeDasharray: "3 3", horizontal: false }),
      /* @__PURE__ */ jsx34(XAxis, { type: "number", ...AXIS_PROPS, tickFormatter: format }),
      /* @__PURE__ */ jsx34(
        YAxis,
        {
          type: "category",
          dataKey: categoryKey,
          ...AXIS_PROPS,
          width: categoryWidth ?? 150
        }
      ),
      /* @__PURE__ */ jsx34(Tooltip2, { content: /* @__PURE__ */ jsx34(ChartTooltip, { format }), cursor: { fill: "transparent" } })
    ] });
  }
  return /* @__PURE__ */ jsxs30(Fragment12, { children: [
    hideGrid ? null : /* @__PURE__ */ jsx34(CartesianGrid, { stroke: GRID_VAR, strokeDasharray: "3 3", vertical: false }),
    /* @__PURE__ */ jsx34(XAxis, { dataKey: categoryKey, ...AXIS_PROPS }),
    hideValueAxis ? null : /* @__PURE__ */ jsx34(
      YAxis,
      {
        yAxisId: rightAxis ? "left" : void 0,
        ...AXIS_PROPS,
        tickFormatter: format,
        width: 48
      }
    ),
    rightAxis ? /* @__PURE__ */ jsx34(
      YAxis,
      {
        yAxisId: "right",
        orientation: "right",
        ...AXIS_PROPS,
        tickFormatter: format,
        width: 48
      }
    ) : null,
    /* @__PURE__ */ jsx34(Tooltip2, { content: /* @__PURE__ */ jsx34(ChartTooltip, { format }), cursor: { stroke: GRID_VAR } })
  ] });
}
function axisId(series, rightAxis) {
  if (!rightAxis) return void 0;
  return series.axis === "right" ? "right" : "left";
}
function usesRightAxis(series) {
  return series.some((s) => s.axis === "right");
}
function useChartParts({
  data,
  series,
  categoryKey,
  label,
  summary,
  format = defaultFormat,
  hideLegend,
  decorative = false
}) {
  const rows = data;
  const resolvedSummary = summary ?? describe(rows, series, categoryKey, format);
  const figures = figuresText(rows, series, categoryKey, format);
  const showLegend = hideLegend === void 0 ? series.length > 1 : !hideLegend;
  return { label, summary: resolvedSummary, figures, format, showLegend, decorative };
}
function LineChart(props) {
  const { data, series, categoryKey, height = 240, hideValueAxis, hideGrid, className } = props;
  const { label, summary, figures, format, showLegend, decorative } = useChartParts(props);
  const rightAxis = usesRightAxis(series) && !hideValueAxis;
  return /* @__PURE__ */ jsxs30("div", { className, children: [
    /* @__PURE__ */ jsx34(
      ChartFrame,
      {
        label,
        summary,
        figures,
        height,
        decorative,
        children: /* @__PURE__ */ jsxs30(RechartsLineChart, { data, children: [
          /* @__PURE__ */ jsx34(
            CartesianFurniture,
            {
              categoryKey,
              format,
              hideValueAxis,
              hideGrid,
              rightAxis
            }
          ),
          series.map((s, index) => /* @__PURE__ */ jsx34(
            Line,
            {
              yAxisId: axisId(s, rightAxis),
              type: "monotone",
              dataKey: s.key,
              name: s.label,
              stroke: seriesColour(s, index),
              strokeWidth: 2,
              dot: data.length <= 24,
              activeDot: { r: 4 },
              connectNulls: false
            },
            s.key
          ))
        ] })
      }
    ),
    showLegend ? /* @__PURE__ */ jsx34(ChartLegend, { series }) : null
  ] });
}
function AreaChart(props) {
  const {
    data,
    series,
    categoryKey,
    height = 240,
    hideValueAxis,
    hideGrid,
    stacked = series.length > 1,
    className
  } = props;
  const { label, summary, figures, format, showLegend, decorative } = useChartParts(props);
  const rightAxis = usesRightAxis(series) && !hideValueAxis;
  return /* @__PURE__ */ jsxs30("div", { className, children: [
    /* @__PURE__ */ jsx34(
      ChartFrame,
      {
        label,
        summary,
        figures,
        height,
        decorative,
        children: /* @__PURE__ */ jsxs30(RechartsAreaChart, { data, children: [
          /* @__PURE__ */ jsx34(
            CartesianFurniture,
            {
              categoryKey,
              format,
              hideValueAxis,
              hideGrid,
              rightAxis
            }
          ),
          series.map((s, index) => /* @__PURE__ */ jsx34(
            Area,
            {
              yAxisId: axisId(s, rightAxis),
              type: "monotone",
              dataKey: s.key,
              name: s.label,
              stackId: stacked ? "stack" : void 0,
              stroke: seriesColour(s, index),
              fill: seriesColour(s, index),
              fillOpacity: 0.15,
              strokeWidth: 2,
              connectNulls: false
            },
            s.key
          ))
        ] })
      }
    ),
    showLegend ? /* @__PURE__ */ jsx34(ChartLegend, { series }) : null
  ] });
}
function BarChart(props) {
  const {
    data,
    series,
    categoryKey,
    height = 240,
    hideValueAxis,
    hideGrid,
    stacked = false,
    horizontal = false,
    categoryWidth,
    className
  } = props;
  const { label, summary, figures, format, showLegend, decorative } = useChartParts(props);
  const rightAxis = usesRightAxis(series) && !hideValueAxis && !horizontal;
  return /* @__PURE__ */ jsxs30("div", { className, children: [
    /* @__PURE__ */ jsx34(
      ChartFrame,
      {
        label,
        summary,
        figures,
        height,
        decorative,
        children: /* @__PURE__ */ jsxs30(RechartsBarChart, { data, layout: horizontal ? "vertical" : void 0, children: [
          /* @__PURE__ */ jsx34(
            CartesianFurniture,
            {
              categoryKey,
              format,
              hideValueAxis,
              hideGrid,
              rightAxis,
              horizontal,
              categoryWidth
            }
          ),
          series.map((s, index) => /* @__PURE__ */ jsx34(
            Bar,
            {
              yAxisId: axisId(s, rightAxis),
              dataKey: s.key,
              name: s.label,
              stackId: stacked ? "stack" : void 0,
              fill: seriesColour(s, index),
              children: s.toneKey ? data.map((datum, rowIndex) => {
                const tone = datum[s.toneKey];
                return /* @__PURE__ */ jsx34(
                  Cell,
                  {
                    fill: typeof tone === "string" && tone in CHART_TONE_VARS ? CHART_TONE_VARS[tone] : seriesColour(s, index)
                  },
                  `${s.key}-${rowIndex}`
                );
              }) : null
            },
            s.key
          ))
        ] })
      }
    ),
    showLegend ? /* @__PURE__ */ jsx34(ChartLegend, { series }) : null
  ] });
}
function DonutChart({
  data,
  label,
  summary,
  height = 240,
  format = defaultFormat,
  filled = false,
  hideLegend = false,
  decorative = false,
  className,
  ...props
}) {
  const total = data.reduce((sum, slice) => sum + (Number.isFinite(slice.value) ? slice.value : 0), 0);
  const resolvedSummary = summary ?? (data.length === 0 ? "no data." : `${format(total)} in total across ${data.length} ${data.length === 1 ? "slice" : "slices"}.`);
  const figures = data.length === 0 ? "No data." : `Figures: ${data.map((slice) => {
    const share = total > 0 ? Math.round(slice.value / total * 100) : 0;
    return `${slice.label} ${format(slice.value)} (${share}%)`;
  }).join("; ")}.`;
  const colour = (slice, index) => slice.colour ?? (slice.tone ? CHART_TONE_VARS[slice.tone] : chartSeriesVar(index));
  return /* @__PURE__ */ jsxs30("div", { className, ...props, children: [
    /* @__PURE__ */ jsx34(
      ChartFrame,
      {
        label,
        summary: resolvedSummary,
        figures,
        height,
        decorative,
        children: /* @__PURE__ */ jsxs30(RechartsPieChart, { children: [
          /* @__PURE__ */ jsx34(Tooltip2, { content: /* @__PURE__ */ jsx34(ChartTooltip, { format }) }),
          /* @__PURE__ */ jsx34(
            Pie,
            {
              data,
              dataKey: "value",
              nameKey: "label",
              innerRadius: filled ? 0 : "55%",
              outerRadius: "80%",
              paddingAngle: 0,
              stroke: SURFACE_VAR,
              children: data.map((slice, index) => /* @__PURE__ */ jsx34(Cell, { fill: colour(slice, index) }, slice.label))
            }
          )
        ] })
      }
    ),
    hideLegend ? null : /* @__PURE__ */ jsx34(
      ChartLegend,
      {
        series: data.map((slice) => ({
          key: slice.label,
          label: slice.label,
          tone: slice.tone,
          colour: slice.colour
        }))
      }
    )
  ] });
}

// src/components/filter-menu.tsx
import { Search, X as X3 } from "lucide-react";
import * as React34 from "react";
import { Fragment as Fragment13, jsx as jsx35, jsxs as jsxs31 } from "react/jsx-runtime";
function selectedValues(value, id) {
  const raw = value?.[id];
  if (Array.isArray(raw)) return raw.filter((v) => typeof v === "string" && v !== "");
  if (typeof raw === "string" && raw !== "") return [raw];
  return [];
}
function isFilterOn(value, id) {
  return value?.[id] === true;
}
function filterChips(filters, value) {
  const chips = [];
  for (const filter of filters) {
    if (filter.kind === "toggle") {
      if (!isFilterOn(value, filter.id)) continue;
      chips.push({
        key: filter.id,
        filterId: filter.id,
        filterLabel: filter.label,
        valueLabel: filter.label,
        label: filter.label,
        removeLabel: `Remove filter ${filter.label}`
      });
      continue;
    }
    for (const selected of selectedValues(value, filter.id)) {
      const valueLabel = filter.options.find((o) => o.value === selected)?.label ?? selected;
      const label = `${filter.label}: ${valueLabel}`;
      chips.push({
        key: `${filter.id}:${selected}`,
        filterId: filter.id,
        value: selected,
        filterLabel: filter.label,
        valueLabel,
        label,
        removeLabel: `Remove filter ${label}`
      });
    }
  }
  return chips;
}
function activeFilterCount(filters, value) {
  return filterChips(filters, value).length;
}
function removeFilterChip(value, chip) {
  const next = { ...value };
  if (chip.value === void 0) {
    delete next[chip.filterId];
    return next;
  }
  const remaining = selectedValues(value, chip.filterId).filter((v) => v !== chip.value);
  if (remaining.length === 0) delete next[chip.filterId];
  else next[chip.filterId] = remaining;
  return next;
}
var ANY = "__any";
function FilterMenu({
  filters,
  value,
  onChange,
  resultCount,
  totalCount,
  resultNoun,
  label = "Filters",
  align = "start",
  open,
  onOpenChange,
  className
}) {
  const generated = React34.useId();
  const chips = React34.useMemo(() => filterChips(filters, value), [filters, value]);
  const count2 = chips.length;
  const noun = resultNoun ?? { one: "result", other: "results" };
  const setFilter = (id, next) => {
    const merged = { ...value };
    const empty = next === void 0 || next === null || next === false || next === "" || Array.isArray(next) && next.length === 0;
    if (empty) delete merged[id];
    else merged[id] = next;
    onChange(merged);
  };
  const clearAll = () => {
    const next = { ...value };
    for (const filter of filters) delete next[filter.id];
    onChange(next);
  };
  const triggerName = count2 > 0 ? `${label}, ${count2} active` : `${label}, none active`;
  const showTotal = count2 > 0 && totalCount !== void 0;
  const nounFor = (n) => n === 1 ? noun.one : noun.other;
  const word = showTotal ? nounFor(totalCount) : nounFor(resultCount);
  return /* @__PURE__ */ jsxs31("div", { className: cn("flex flex-wrap items-center gap-3", className), children: [
    /* @__PURE__ */ jsxs31(Popover, { open, onOpenChange, children: [
      /* @__PURE__ */ jsx35(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs31(Button, { variant: "secondary", size: "sm", "aria-label": triggerName, children: [
        /* @__PURE__ */ jsx35(Search, { "aria-hidden": "true" }),
        count2 > 0 ? /* @__PURE__ */ jsx35("span", { className: "font-mono", "data-numeric": true, children: count2 }) : /* @__PURE__ */ jsx35("span", { children: label })
      ] }) }),
      /* @__PURE__ */ jsx35(PopoverContent, { "aria-label": label, align, className: "max-h-96 w-80 overflow-y-auto", children: /* @__PURE__ */ jsx35("div", { className: "flex min-w-0 flex-col gap-6", children: filters.map((filter) => /* @__PURE__ */ jsx35(
        FilterGroup,
        {
          filter,
          value,
          idPrefix: `${generated}-${filter.id}`,
          onSet: setFilter
        },
        filter.id
      )) }) })
    ] }),
    /* @__PURE__ */ jsxs31("p", { role: "status", className: "whitespace-nowrap text-caption text-text-muted", children: [
      /* @__PURE__ */ jsx35("span", { className: "font-mono", "data-numeric": true, children: resultCount }),
      showTotal ? /* @__PURE__ */ jsxs31(Fragment13, { children: [
        " of ",
        /* @__PURE__ */ jsx35("span", { className: "font-mono", "data-numeric": true, children: totalCount })
      ] }) : null,
      " ",
      /* @__PURE__ */ jsx35("span", { className: showTotal ? "sr-only" : void 0, children: word })
    ] }),
    count2 > 0 ? /* @__PURE__ */ jsx35("ul", { className: "m-0 flex list-none flex-wrap items-center gap-2 p-0", children: chips.map((chip) => /* @__PURE__ */ jsx35("li", { children: /* @__PURE__ */ jsxs31("span", { className: "inline-flex items-center gap-1 border border-divider py-1 pl-2 pr-1 text-caption text-text", children: [
      chip.label,
      /* @__PURE__ */ jsx35(
        "button",
        {
          type: "button",
          "aria-label": chip.removeLabel,
          onClick: () => onChange(removeFilterChip(value, chip)),
          className: cn(
            // 1.5rem square, so the target clears SC 2.5.8 even though
            // the glyph inside is 0.75rem.
            "inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-text-muted",
            "transition-colors duration-fast ease-move hover:text-text",
            FOCUS_RING
          ),
          children: /* @__PURE__ */ jsx35(X3, { className: "size-3", "aria-hidden": "true" })
        }
      )
    ] }) }, chip.key)) }) : null,
    count2 > 1 ? /* @__PURE__ */ jsx35(Button, { variant: "link", size: "sm", onClick: clearAll, children: "Clear all" }) : null
  ] });
}
FilterMenu.displayName = "FilterMenu";
function FilterGroup({ filter, value, idPrefix, onSet }) {
  if (filter.kind === "toggle") {
    return /* @__PURE__ */ jsx35(
      Checkbox,
      {
        checked: isFilterOn(value, filter.id),
        onCheckedChange: (checked) => onSet(filter.id, checked === true),
        label: filter.label,
        description: filter.description
      }
    );
  }
  if (filter.kind === "single") {
    const current = selectedValues(value, filter.id)[0] ?? ANY;
    const labelId = `${idPrefix}-label`;
    return /* @__PURE__ */ jsxs31("div", { className: "flex min-w-0 flex-col gap-2", children: [
      /* @__PURE__ */ jsx35("p", { id: labelId, className: "m-0 eyebrow", children: filter.label }),
      filter.options.length === 0 ? /* @__PURE__ */ jsx35(NoOptions, {}) : /* @__PURE__ */ jsxs31(
        RadioGroup,
        {
          "aria-labelledby": labelId,
          value: current,
          onValueChange: (next) => onSet(filter.id, next === ANY ? void 0 : next),
          children: [
            /* @__PURE__ */ jsx35(RadioGroupItem, { value: ANY, label: filter.anyLabel ?? "Any" }),
            filter.options.map((option) => /* @__PURE__ */ jsx35(
              RadioGroupItem,
              {
                value: option.value,
                label: /* @__PURE__ */ jsx35(OptionLabel, { option }),
                description: option.description
              },
              option.value
            ))
          ]
        }
      )
    ] });
  }
  const selected = selectedValues(value, filter.id);
  return /* @__PURE__ */ jsxs31("fieldset", { className: "flex min-w-0 flex-col gap-2", children: [
    /* @__PURE__ */ jsx35("legend", { className: "eyebrow mb-2", children: filter.label }),
    filter.options.length === 0 ? /* @__PURE__ */ jsx35(NoOptions, {}) : filter.options.map((option) => /* @__PURE__ */ jsx35(
      Checkbox,
      {
        checked: selected.includes(option.value),
        onCheckedChange: (checked) => onSet(
          filter.id,
          checked === true ? [...selected, option.value] : selected.filter((v) => v !== option.value)
        ),
        label: /* @__PURE__ */ jsx35(OptionLabel, { option }),
        description: option.description
      },
      option.value
    ))
  ] });
}
function OptionLabel({ option }) {
  if (option.count === void 0) return /* @__PURE__ */ jsx35(Fragment13, { children: option.label });
  return /* @__PURE__ */ jsxs31("span", { className: "inline-flex items-baseline gap-2", children: [
    /* @__PURE__ */ jsx35("span", { children: option.label }),
    /* @__PURE__ */ jsxs31("span", { className: "font-mono text-caption text-text-muted", "data-numeric": true, children: [
      option.count,
      /* @__PURE__ */ jsx35("span", { className: "sr-only", children: " matching" })
    ] })
  ] });
}
function NoOptions() {
  return /* @__PURE__ */ jsx35("p", { className: "m-0 text-caption text-text-muted", children: "No options" });
}

// src/components/sparkline.tsx
import * as React35 from "react";
import { Fragment as Fragment14, jsx as jsx36, jsxs as jsxs32 } from "react/jsx-runtime";
var TONE_CLASS2 = {
  compliant: "text-status-compliant",
  warning: "text-status-warning",
  degraded: "text-status-degraded",
  breach: "text-status-breach",
  "not-assessed": "text-status-not-assessed"
};
var STROKE_WIDTH = 1.5;
var MARKER_RADIUS = 2;
var NO_DATA_RULE = 8;
function round(value) {
  return Math.round(value * 100) / 100;
}
function toReading(datum) {
  if (typeof datum === "number") {
    return { value: Number.isFinite(datum) ? datum : null };
  }
  if (datum && typeof datum === "object") {
    const { value, label } = datum;
    return { value: typeof value === "number" && Number.isFinite(value) ? value : null, label };
  }
  return { value: null };
}
function defaultFormat2(value) {
  return Number.isInteger(value) ? String(value) : String(Number.parseFloat(value.toPrecision(6)));
}
function describe2(readings, format) {
  const values = [];
  for (const reading of readings) {
    if (reading.value !== null) values.push(reading.value);
  }
  const gaps = readings.length - values.length;
  const missing = gaps > 0 ? ` ${gaps} reading${gaps === 1 ? "" : "s"} missing.` : "";
  if (values.length === 0) return "no data.";
  if (values.length === 1) return `one reading, ${format(values[0])}.${missing}`;
  const first = values[0];
  const last = values[values.length - 1];
  let low = first;
  let high = first;
  for (const value of values) {
    if (value < low) low = value;
    if (value > high) high = value;
  }
  const span = `across ${values.length} readings`;
  if (low === high) return `flat at ${format(first)} ${span}.${missing}`;
  const range = `; low ${format(low)}, high ${format(high)}.`;
  if (last > first) return `rises from ${format(first)} to ${format(last)} ${span}${range}${missing}`;
  if (last < first) return `falls from ${format(first)} to ${format(last)} ${span}${range}${missing}`;
  return `starts and ends at ${format(first)} ${span}${range}${missing}`;
}
function readingsText(readings, format) {
  if (readings.length === 0) return "No readings.";
  const parts = readings.map((reading) => {
    const figure = reading.value === null ? "no reading" : format(reading.value);
    return reading.label ? `${reading.label} ${figure}` : figure;
  });
  return `Readings: ${parts.join(", ")}.`;
}
var SparklineImpl = React35.forwardRef(
  ({
    data,
    label,
    decorative = false,
    tone,
    width = 72,
    height = 20,
    marker = true,
    format = defaultFormat2,
    unit,
    summary,
    className,
    ...props
  }, ref) => {
    const generated = React35.useId();
    const descriptionId = `sparkline-${generated}-readings`;
    const readings = React35.useMemo(() => data.map(toReading), [data]);
    const withUnit = React35.useCallback(
      (value) => `${format(value)}${unit ?? ""}`,
      [format, unit]
    );
    const pad = marker ? Math.max(STROKE_WIDTH / 2, MARKER_RADIUS) : STROKE_WIDTH / 2;
    const innerWidth = Math.max(width - pad * 2, 0);
    const innerHeight = Math.max(height - pad * 2, 0);
    const values = [];
    for (const reading of readings) {
      if (reading.value !== null) values.push(reading.value);
    }
    let low = values[0] ?? 0;
    let high = values[0] ?? 0;
    for (const value of values) {
      if (value < low) low = value;
      if (value > high) high = value;
    }
    const count2 = readings.length;
    const x = (index) => count2 <= 1 ? pad + innerWidth / 2 : pad + index / (count2 - 1) * innerWidth;
    const y = (value) => high === low ? pad + innerHeight / 2 : pad + (1 - (value - low) / (high - low)) * innerHeight;
    const subpaths = [];
    let run = [];
    const flush = () => {
      if (run.length === 0) return;
      const [head, ...tail] = run;
      subpaths.push(`M ${head} L ${tail.length > 0 ? tail.join(" L ") : head}`);
      run = [];
    };
    readings.forEach((reading, index) => {
      if (reading.value === null) {
        flush();
        return;
      }
      run.push(`${round(x(index))} ${round(y(reading.value))}`);
    });
    flush();
    let lastIndex = -1;
    for (let index = readings.length - 1; index >= 0; index -= 1) {
      if (readings[index].value !== null) {
        lastIndex = index;
        break;
      }
    }
    const hasData = values.length > 0;
    const accessibleName = decorative ? void 0 : `${label}: ${summary ?? describe2(readings, withUnit)}`;
    return /* @__PURE__ */ jsxs32(
      "svg",
      {
        ref,
        role: decorative ? void 0 : "img",
        "aria-hidden": decorative ? true : void 0,
        "aria-label": accessibleName,
        "aria-describedby": decorative ? void 0 : descriptionId,
        "data-tone": tone,
        width,
        height,
        viewBox: `0 0 ${width} ${height}`,
        preserveAspectRatio: "xMidYMid meet",
        className: cn("inline-block shrink-0 align-middle", tone && TONE_CLASS2[tone], className),
        ...props,
        children: [
          decorative ? null : /* @__PURE__ */ jsx36("desc", { id: descriptionId, children: readingsText(readings, withUnit) }),
          hasData ? /* @__PURE__ */ jsxs32(Fragment14, { children: [
            /* @__PURE__ */ jsx36(
              "path",
              {
                d: subpaths.join(" "),
                fill: "none",
                stroke: "currentColor",
                strokeWidth: STROKE_WIDTH,
                strokeLinecap: "round",
                strokeLinejoin: "round",
                vectorEffect: "non-scaling-stroke"
              }
            ),
            marker && lastIndex >= 0 ? /* @__PURE__ */ jsx36(
              "circle",
              {
                cx: round(x(lastIndex)),
                cy: round(y(readings[lastIndex].value)),
                r: MARKER_RADIUS,
                fill: "currentColor"
              }
            ) : null
          ] }) : /* @__PURE__ */ jsx36(
            "line",
            {
              className: "text-status-not-assessed",
              x1: round(width / 2 - Math.min(NO_DATA_RULE, innerWidth) / 2),
              x2: round(width / 2 + Math.min(NO_DATA_RULE, innerWidth) / 2),
              y1: round(height / 2),
              y2: round(height / 2),
              stroke: "currentColor",
              strokeWidth: STROKE_WIDTH,
              strokeDasharray: "2 2",
              vectorEffect: "non-scaling-stroke"
            }
          )
        ]
      }
    );
  }
);
SparklineImpl.displayName = "Sparkline";
var Sparkline = SparklineImpl;

// src/components/config-sequence.tsx
import { ChevronRight as ChevronRight2 } from "lucide-react";
import * as React36 from "react";
import { Fragment as Fragment15, jsx as jsx37, jsxs as jsxs33 } from "react/jsx-runtime";
var CONFIG_SEQUENCE_WORD = {
  complete: "Configured",
  outstanding: "Not configured"
};
var STATE_TONE = {
  complete: "compliant",
  outstanding: "not-assessed"
};
var REQUIREMENT_WORD = { required: "Required", optional: "Optional" };
var DENSITY = {
  header: {
    list: "gap-x-2 gap-y-1.5",
    item: "gap-x-2 gap-y-0.5",
    label: "text-body-sm",
    qualifier: "text-caption",
    chevron: "size-3.5"
  },
  body: {
    list: "gap-x-3 gap-y-2",
    item: "gap-x-2.5 gap-y-1",
    label: "text-body",
    qualifier: "text-body-sm",
    chevron: "size-4"
  }
};
var ConfigSequence = React36.forwardRef(
  ({
    items,
    density = "body",
    renderLink,
    className,
    "aria-label": ariaLabel = "Configuration sequence",
    ...props
  }, ref) => {
    if (items.length === 0) return null;
    const scale = DENSITY[density];
    const link = renderLink ?? (({ href, ...rest }) => /* @__PURE__ */ jsx37("a", { href, ...rest }));
    return (
      // role="list" because Tailwind's reset takes list-style off every ol,
      // and Safari drops the list semantics with it.
      /* @__PURE__ */ jsx37(
        "ol",
        {
          ref,
          role: "list",
          "aria-label": ariaLabel,
          "data-density": density,
          className: cn("flex min-w-0 flex-wrap items-center", scale.list, className),
          ...props,
          children: items.map((item, index) => {
            const requirement = item.optional ? "optional" : "required";
            const current = item.current ? "step" : void 0;
            const interactive = Boolean(item.href || item.onSelect);
            const body = /* @__PURE__ */ jsxs33(Fragment15, { children: [
              /* @__PURE__ */ jsx37(
                "span",
                {
                  className: cn(
                    "min-w-0 break-words",
                    scale.label,
                    item.current && "font-medium",
                    // The underline is the affordance, and it belongs to the
                    // name alone: run it under the whole step and the state
                    // word gets underlined too, which reads as a second link.
                    interactive && "underline-offset-4 group-hover/step:underline"
                  ),
                  children: item.label
                }
              ),
              /* @__PURE__ */ jsx37("span", { className: cn(scale.qualifier, "text-text-muted"), children: REQUIREMENT_WORD[requirement] }),
              /* @__PURE__ */ jsx37(Status, { tone: STATE_TONE[item.state], children: item.stateLabel ?? CONFIG_SEQUENCE_WORD[item.state] })
            ] });
            const shared = cn("flex min-w-0 flex-wrap items-center text-text", scale.item);
            const actionable = cn(
              shared,
              "group/step rounded-sm text-left",
              "transition-colors duration-fast ease-move",
              FOCUS_RING
            );
            return /* @__PURE__ */ jsxs33(
              "li",
              {
                "data-state": item.state,
                "data-requirement": requirement,
                "aria-current": interactive ? void 0 : current,
                className: cn("flex min-w-0 items-center", scale.list),
                children: [
                  index > 0 ? /* @__PURE__ */ jsx37(
                    ChevronRight2,
                    {
                      "aria-hidden": "true",
                      className: cn("shrink-0 text-text-muted", scale.chevron)
                    }
                  ) : null,
                  item.href ? link({
                    href: item.href,
                    className: actionable,
                    "aria-current": current,
                    onClick: item.onSelect,
                    children: body
                  }) : item.onSelect ? /* @__PURE__ */ jsx37(
                    "button",
                    {
                      type: "button",
                      "aria-current": current,
                      onClick: item.onSelect,
                      className: actionable,
                      children: body
                    }
                  ) : /* @__PURE__ */ jsx37("span", { className: shared, children: body })
                ]
              },
              item.id
            );
          })
        }
      )
    );
  }
);
ConfigSequence.displayName = "ConfigSequence";
export {
  AI_MARKER_WORD,
  AiMarker,
  AppHeader,
  AreaChart,
  AuthShell,
  BarChart,
  Button,
  CHART_SERIES_VARS,
  CHART_TONE_VARS,
  CONFIG_SEQUENCE_WORD,
  Callout,
  ChartFrame,
  ChartLegend,
  ChartTooltip,
  Checkbox,
  ConfigSequence,
  ConfirmDialog,
  DataTable,
  DetailField,
  DetailList,
  Dialog,
  DialogClose,
  DialogContent,
  DialogPortal,
  DialogTrigger,
  DonutChart,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  EmptyState,
  FOCUS_RING,
  FOCUS_RING_WITHIN,
  Field,
  FieldGroup,
  FilterMenu,
  FormDialog,
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
  Input,
  Label,
  LineChart,
  MachineGenerated,
  Metric,
  MetricRow,
  NavShell,
  OVERLAY_SURFACE,
  PageHeader,
  PermissionGate,
  Popover,
  PopoverAnchor,
  PopoverClose,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
  Prose,
  RadioGroup,
  RadioGroupItem,
  STATUS_GLYPHS,
  ScrollArea,
  ScrollBar,
  Section,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Skeleton,
  SkeletonLines,
  Sparkline,
  Status,
  Stepper,
  Textarea,
  ThemeToggle,
  Toast,
  ToastAction,
  Toaster,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
  WizardDialog,
  activeFilterCount,
  buildHubs,
  buttonVariants,
  chartSeriesVar,
  clearColumnVisibility,
  cn,
  columnVisibilityStorageKey,
  dismissAllToasts,
  filterChips,
  flattenSteps,
  isFilterOn,
  labelVariants,
  loadColumnVisibility,
  matchesPath,
  removeFilterChip,
  resolveActiveNav,
  saveColumnVisibility,
  selectedValues,
  toast,
  toastVariants,
  truncateCell,
  useFieldContext,
  useFieldControl,
  useRecentEntries,
  useRecordRecent,
  useTheme,
  useToast
};
//# sourceMappingURL=index.js.map